/**
 * Shared editable post grid used by CommunityPostCreate and GearSwapPostCreate.
 * Props: items, setItems, widgetTypes, onFileUpload, accentColor, cellSize, interactionMode
 */
import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Platform,
  Pressable,
  ActivityIndicator,
  Image,
  LayoutAnimation,
  Animated,
  ScrollView,
  Linking,
  Modal,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { getFirestore, collection, getDocs } from 'firebase/firestore';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensors,
  useSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import Slider from '@react-native-community/slider';
import {
  buildGridFromItems,
  placeAndReflow,
  moveRowBand,
  GRID_COLS,
  GRID_ROWS,
  EMPTY_WIDGET_TYPE,
  getRowHeightMultipliers,
  resolveCellBorderRadiusPx,
} from '../utils/postGridEngine';
import {
  LINK_DISPLAY_URL,
  LINK_DISPLAY_BUTTON,
  LINK_SHAPE_PILL,
  LINK_SHAPE_ROUNDED,
  LINK_SHAPE_SQUARE,
  DEFAULT_LINK_BUTTON_BG,
  DEFAULT_LINK_BUTTON_FG,
  getLinkDisplayMode,
  getLinkButtonLabel,
  getLinkButtonBg,
  getLinkButtonFg,
  getLinkButtonShape,
  getLinkButtonSize,
  getLinkButtonMetrics,
  linkButtonShapeToBorderRadius,
  LINK_BUTTON_SIZE_SM,
  LINK_BUTTON_SIZE_MD,
  LINK_BUTTON_SIZE_LG,
  DEFAULT_LINK_BUTTON_SIZE,
} from '../utils/postGridLink';
import { resolvePostGridFontFamily } from './PostGridDisplay';
import { getResponsiveValue } from '../styles/breakpoints';
import { MAPBOX_ACCESS_TOKEN } from '../config/mapbox-config';
import { CompactAudioPlayer } from './CompactAudioPlayer';
import { transparentTextInputStyle } from '../utils/transparentTextInputStyle';

// ─── Constants ──────────────────────────────────────────────────────────────

const MERGE_OPTIONS = [
  { id: 'up', label: 'Up', canKey: 'canMergeUp', onKey: 'onMergeUp', icon: 'chevron-up' },
  { id: 'down', label: 'Down', canKey: 'canMergeDown', onKey: 'onMergeDown', icon: 'chevron-down' },
  { id: 'left', label: 'Left', canKey: 'canMergeLeft', onKey: 'onMergeLeft', icon: 'chevron-back' },
  { id: 'right', label: 'Right', canKey: 'canMergeRight', onKey: 'onMergeRight', icon: 'chevron-forward' },
];

const CELL_BG_SWATCHES = [
  null,       // clear
  '#ffffff',
  '#1a1a1a',
  '#f5f0e8',
  '#ffeb3b',
  '#4caf50',
  '#2196f3',
  '#f44336',
  '#ff9800',
  '#e91e63',
  '#9c27b0',
  '#00bcd4',
];

const FONT_SIZE_OPTIONS = [
  { id: 'sm', label: 'S' },
  { id: 'md', label: 'M' },
  { id: 'lg', label: 'L' },
  { id: 'xl', label: 'XL' },
];

const FONT_FAMILY_OPTIONS = [
  { id: null, label: 'Default' },
  { id: 'Arial', label: 'Arial' },
  { id: 'VastagoGrotesk', label: 'Vastago' },
  { id: 'Glacial Indifference', label: 'Glacial' },
  { id: 'Computer Says No', label: 'Computer' },
];

const TEXT_ALIGN_OPTIONS = [
  { id: 'left', label: 'Left', icon: 'text-outline' },
  { id: 'center', label: 'Center', icon: 'reorder-three-outline' },
  { id: 'right', label: 'Right', icon: 'menu-outline' },
];

const LINK_BUTTON_BG_SWATCHES = [
  DEFAULT_LINK_BUTTON_BG,
  '#1a1a1a',
  '#e74c3c',
  '#27ae60',
  '#9b27b0',
  '#f39c12',
  '#00bcd4',
  '#ffffff',
  '#555555',
];

const LINK_BUTTON_FG_SWATCHES = [
  DEFAULT_LINK_BUTTON_FG,
  '#000000',
  '#555555',
  '#f5f0e8',
  '#fbcd9d',
  '#1075ae',
  '#e74c3c',
];

const LINK_DISPLAY_MODE_PICKER = [
  { id: LINK_DISPLAY_URL, label: 'URL' },
  { id: LINK_DISPLAY_BUTTON, label: 'Button' },
];

const LINK_SHAPE_PICKER = [
  { id: LINK_SHAPE_PILL, label: 'Pill' },
  { id: LINK_SHAPE_ROUNDED, label: 'Rounded' },
  { id: LINK_SHAPE_SQUARE, label: 'Square' },
];

const LINK_BUTTON_SIZE_PICKER = [
  { id: LINK_BUTTON_SIZE_SM, label: 'S' },
  { id: LINK_BUTTON_SIZE_MD, label: 'M' },
  { id: LINK_BUTTON_SIZE_LG, label: 'L' },
];

const FONT_COLOR_SWATCHES = [
  null,       // default (inherit / dark)
  '#000000',
  '#ffffff',
  '#555555',
  '#f5f0e8',
  '#1075ae',
  '#e74c3c',
  '#f39c12',
  '#27ae60',
  '#8e44ad',
  '#00bcd4',
  '#fbcd9d',
];

const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function cellPixelHeight(y, rowSpan, cellSize, rowHeightMultipliers) {
  let h = 0;
  for (let r = y; r < y + rowSpan; r++) {
    h += cellSize * (rowHeightMultipliers[r] ?? 1);
  }
  return h;
}

const toIsoDate = (date) => {
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, '0');
  const d = `${date.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const parseIsoDate = (value) => {
  if (!value || typeof value !== 'string') return null;
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

const parseCalendarValue = (value) => {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch (_) {
    const legacyDate = parseIsoDate(value);
    if (!legacyDate) return null;
    return { date: toIsoDate(legacyDate) };
  }
};

const formatFriendlyDate = (isoDate) => {
  const date = parseIsoDate(isoDate);
  if (!date) return '';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const normalizeCalendarSelection = (parsed) => {
  if (!parsed) return { startDate: '', endDate: '' };
  const startDate = parsed.startDate || parsed.date || '';
  const endDate = parsed.endDate || '';
  return { startDate, endDate };
};

const formatCalendarDisplayText = (parsed) => {
  const { startDate, endDate } = normalizeCalendarSelection(parsed);
  if (!startDate) return '';
  if (!endDate) return formatFriendlyDate(startDate);
  const start = parseIsoDate(startDate);
  const end = parseIsoDate(endDate);
  if (!start || !end) return '';
  const sameYear = start.getFullYear() === end.getFullYear();
  const sameMonth = sameYear && start.getMonth() === end.getMonth();
  if (sameMonth) {
    return `${start.toLocaleDateString(undefined, { month: 'short' })} ${start.getDate()}-${end.getDate()}, ${end.getFullYear()}`;
  }
  if (sameYear) {
    return `${start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
  }
  return `${formatFriendlyDate(startDate)} - ${formatFriendlyDate(endDate)}`;
};

// ─── Map Cell (editable) ────────────────────────────────────────────────────

const MAP_SEARCH_LIMIT = 6;

function MapCellEdit({ value, onValueChange, id, accentColor, gridBgColor, isSelected }) {
  const [address, setAddress] = useState(() => {
    try { return JSON.parse(value)?.address || ''; } catch { return ''; }
  });
  const [searchMode, setSearchMode] = useState('venues'); // 'venues' | 'locations'
  const [searching, setSearching] = useState(false);
  const [venueResults, setVenueResults] = useState([]);
  const [results, setResults] = useState([]);

  const runSearch = useCallback(async (queryText, mode) => {
    const trimmed = (queryText || '').trim();
    if (trimmed.length < 2) {
      setVenueResults([]);
      setResults([]);
      return;
    }
    setSearching(true);
    const lower = trimmed.toLowerCase();
    try {
      if (mode === 'venues') {
        setResults([]);
        const snap = await getDocs(collection(getFirestore(), 'venues'));
        const matches = [];
        snap.forEach((d) => {
          const v = d.data();
          const name = (v.name || v.venueName || '').toLowerCase();
          const city = (v.city || '').toLowerCase();
          const state = (v.state || '').toLowerCase();
          const affiliate = (v.affiliate || '').toLowerCase();
          if (
            name.includes(lower) ||
            city.includes(lower) ||
            state.includes(lower) ||
            affiliate.includes(lower) ||
            d.id.toLowerCase().includes(lower)
          ) {
            matches.push({
              id: d.id,
              label: [v.name || v.venueName || d.id, v.city, v.state].filter(Boolean).join(', '),
              lat: v.latitude != null ? parseFloat(v.latitude) : (v.lat != null ? parseFloat(v.lat) : null),
              lng: v.longitude != null ? parseFloat(v.longitude) : (v.lng != null ? parseFloat(v.lng) : null),
            });
          }
        });
        matches.sort((a, b) => a.label.localeCompare(b.label));
        setVenueResults(matches.slice(0, 8));
      } else {
        setVenueResults([]);
        const geoRes = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(trimmed)}.json?access_token=${MAPBOX_ACCESS_TOKEN}&limit=${MAP_SEARCH_LIMIT}`
        ).then(r => r.json()).catch(() => ({ features: [] }));
        setResults(geoRes.features || []);
      }
    } catch (_) {
      if (mode === 'venues') setVenueResults([]);
      else setResults([]);
    }
    setSearching(false);
  }, []);

  const handleSearch = useCallback(() => {
    runSearch(address, searchMode);
  }, [address, searchMode, runSearch]);

  useEffect(() => {
    if (address.trim().length < 2) {
      setVenueResults([]);
      setResults([]);
      return;
    }
    const timer = setTimeout(() => runSearch(address, searchMode), 350);
    return () => clearTimeout(timer);
  }, [address, searchMode, runSearch]);

  const handleSelectResult = useCallback((feature) => {
    const [lng, lat] = feature.center;
    const label = feature.place_name;
    onValueChange(id, JSON.stringify({ lat, lng, zoom: 13, label, address: label }));
    setAddress(label);
    setVenueResults([]);
    setResults([]);
  }, [id, onValueChange]);

  const handleSelectVenue = useCallback((venue) => {
    const label = venue.label;
    const lat = venue.lat;
    const lng = venue.lng;
    if (lat != null && lng != null) {
      onValueChange(id, JSON.stringify({ lat, lng, zoom: 13, label, address: label }));
    } else {
      onValueChange(id, JSON.stringify({ lat: null, lng: null, zoom: 13, label, address: label }));
    }
    setAddress(label);
    setVenueResults([]);
    setResults([]);
  }, [id, onValueChange]);

  const switchSearchMode = useCallback((mode) => {
    setSearchMode(mode);
    setVenueResults([]);
    setResults([]);
    if (address.trim().length >= 2) {
      runSearch(address, mode);
    }
  }, [address, runSearch]);

  let mapPreviewUrl = null;
  try {
    const parsed = JSON.parse(value);
    if (parsed?.lat != null && parsed?.lng != null) {
      mapPreviewUrl = `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/pin-s+e74c3c(${parsed.lng},${parsed.lat})/${parsed.lng},${parsed.lat},${parsed.zoom || 13}/200x200@2x?access_token=${MAPBOX_ACCESS_TOKEN}`;
    }
  } catch (_) {}

  if (mapPreviewUrl) {
    return (
      <View style={styles.mapCellFilled}>
        <Image source={{ uri: mapPreviewUrl }} style={styles.mapPreviewImage} resizeMode="cover" />
        <TouchableOpacity
          style={styles.mediaRemoveBtn}
          onPress={() => { onValueChange(id, ''); setAddress(''); setVenueResults([]); setResults([]); }}
        >
          <Ionicons name="close-circle" size={18} color="rgba(0,0,0,0.6)" />
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={styles.mapCellRoot}>
      <View style={styles.mapModeToggle}>
        <TouchableOpacity
          style={[styles.mapModeBtn, searchMode === 'venues' && styles.mapModeBtnActive]}
          onPress={() => switchSearchMode('venues')}
        >
          <Text style={[styles.mapModeBtnText, searchMode === 'venues' && styles.mapModeBtnTextActive]}>Venues</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.mapModeBtn, searchMode === 'locations' && styles.mapModeBtnActive]}
          onPress={() => switchSearchMode('locations')}
        >
          <Text style={[styles.mapModeBtnText, searchMode === 'locations' && styles.mapModeBtnTextActive]}>Locations</Text>
        </TouchableOpacity>
      </View>
      <View style={styles.mapInputWrap}>
        <Ionicons name={searchMode === 'venues' ? 'business-outline' : 'map-outline'} size={14} color="#999" />
        <TextInput
          style={styles.mapInput}
          placeholder={searchMode === 'venues' ? 'Search venues…' : 'Search address…'}
          value={address}
          onChangeText={(t) => { setAddress(t); setVenueResults([]); setResults([]); }}
          placeholderTextColor="#bbb"
          onSubmitEditing={handleSearch}
          returnKeyType="search"
        />
        <TouchableOpacity onPress={handleSearch} disabled={searching} style={styles.mapSearchBtn}>
          {searching
            ? <ActivityIndicator size="small" color={accentColor} />
            : <Ionicons name="search" size={14} color={accentColor} />
          }
        </TouchableOpacity>
      </View>
      {isSelected && (
        <>
          {searchMode === 'venues' && venueResults.length > 0 && (
            <View style={styles.mapResultsDropdown}>
              <ScrollView
                style={[styles.mapResultsScroll, (gridBgColor && { backgroundColor: gridBgColor })]}
                contentContainerStyle={styles.mapResultsContent}
                keyboardShouldPersistTaps="handled"
                nestedScrollEnabled
                showsVerticalScrollIndicator={true}
              >
                {venueResults.map((venue) => (
                  <TouchableOpacity
                    key={venue.id}
                    style={[styles.mapResultItem, { borderLeftWidth: 2, borderLeftColor: '#3ba378' }]}
                    onPress={() => handleSelectVenue(venue)}
                    activeOpacity={0.7}
                  >
                    <Ionicons name="location" size={14} color="#3ba378" />
                    <Text style={styles.mapResultText} numberOfLines={2}>{venue.label}</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          )}
          {searchMode === 'venues' && address.trim().length >= 2 && !searching && venueResults.length === 0 && (
            <View style={styles.mapResultsDropdown}>
              <Text style={styles.mapEmptyResultsText}>No venues found — try Locations</Text>
            </View>
          )}
          {searchMode === 'locations' && results.length > 0 && (
            <View style={styles.mapResultsDropdown}>
              <ScrollView
                style={[styles.mapResultsScroll, (gridBgColor && { backgroundColor: gridBgColor })]}
                contentContainerStyle={styles.mapResultsContent}
                keyboardShouldPersistTaps="handled"
                nestedScrollEnabled
                showsVerticalScrollIndicator={true}
              >
                {results.map((feature, i) => (
                  <TouchableOpacity
                    key={feature.id || i}
                    style={styles.mapResultItem}
                    onPress={() => handleSelectResult(feature)}
                    activeOpacity={0.7}
                  >
                    <Ionicons name="location" size={14} color={accentColor} />
                    <Text style={styles.mapResultText} numberOfLines={2}>{feature.place_name}</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          )}
        </>
      )}
    </View>
  );
}

function CalendarPickerModal({
  visible,
  monthDate,
  startIsoDate,
  endIsoDate,
  pendingRangeStartIso,
  rangeMode,
  accentColor,
  onPrevMonth,
  onNextMonth,
  onToggleRangeMode,
  onClose,
  onSelectDate,
  onClearDate,
}) {
  const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const monthEnd = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
  const firstWeekday = monthStart.getDay();
  const daysInMonth = monthEnd.getDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let day = 1; day <= daysInMonth; day++) cells.push(day);
  while (cells.length % 7 !== 0) cells.push(null);

  const monthTitle = monthDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable style={styles.calendarModalOverlay} onPress={onClose}>
        <Pressable style={styles.calendarModalCard} onPress={(e) => e.stopPropagation()}>
          <View style={styles.calendarModalHeader}>
            <TouchableOpacity onPress={onPrevMonth} style={styles.calendarNavBtn}>
              <Ionicons name="chevron-back" size={18} color={accentColor} />
            </TouchableOpacity>
            <Text style={styles.calendarModalTitle}>{monthTitle}</Text>
            <TouchableOpacity onPress={onNextMonth} style={styles.calendarNavBtn}>
              <Ionicons name="chevron-forward" size={18} color={accentColor} />
            </TouchableOpacity>
          </View>

          <View style={styles.calendarWeekRow}>
            {WEEKDAY_LABELS.map((d) => (
              <Text key={d} style={styles.calendarWeekLabel}>{d}</Text>
            ))}
          </View>

          <View style={styles.calendarGrid}>
            {cells.map((day, index) => {
              if (!day) return <View key={`blank-${index}`} style={styles.calendarDayCell} />;
              const iso = toIsoDate(new Date(monthDate.getFullYear(), monthDate.getMonth(), day));
              const isStart = iso === startIsoDate || iso === pendingRangeStartIso;
              const isEnd = iso === endIsoDate;
              const hasRange = !!startIsoDate && !!endIsoDate;
              const isInRange = hasRange && iso > startIsoDate && iso < endIsoDate;
              const isSelected = isStart || isEnd;
              return (
                <TouchableOpacity
                  key={`day-${iso}`}
                  style={[
                    styles.calendarDayCell,
                    styles.calendarDayButton,
                    isInRange && { backgroundColor: `${accentColor}44` },
                    isSelected && { backgroundColor: accentColor },
                  ]}
                  onPress={() => onSelectDate(iso)}
                >
                  <Text style={[styles.calendarDayText, isSelected && styles.calendarDayTextSelected]}>
                    {day}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>

          <View style={styles.calendarRangeControls}>
            <TouchableOpacity
              onPress={onToggleRangeMode}
              style={[styles.calendarRangeToggle, rangeMode && { backgroundColor: accentColor }]}
            >
              <Ionicons name="swap-horizontal-outline" size={14} color={rangeMode ? '#fff' : accentColor} />
              <Text style={[styles.calendarRangeToggleText, rangeMode && { color: '#fff' }]}>
                {rangeMode ? 'Range Mode' : 'Single Date'}
              </Text>
            </TouchableOpacity>
            {rangeMode && pendingRangeStartIso ? (
              <Text style={styles.calendarPendingText}>Select end date...</Text>
            ) : null}
          </View>

          <View style={styles.calendarModalFooter}>
            <TouchableOpacity onPress={onClearDate} style={styles.calendarFooterBtn}>
              <Text style={styles.calendarFooterText}>Clear</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={onClose} style={styles.calendarFooterBtn}>
              <Text style={styles.calendarFooterText}>Close</Text>
            </TouchableOpacity>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

// ─── Widget Type Picker ──────────────────────────────────────────────────────

function WidgetTypePicker({
  cellId,
  currentType,
  widgetTypes,
  accentColor,
  cellBg,
  fontSize,
  textAlign,
  textColor,
  fontFamily,
  onSelect,
  onClose,
  onUnmerge,
  canUnmerge,
  onMergeLeft,
  onMergeRight,
  onMergeUp,
  onMergeDown,
  canMergeLeft,
  canMergeRight,
  canMergeUp,
  canMergeDown,
  onCellBgChange,
  onFontSizeChange,
  onTextAlignChange,
  onFontColorChange,
  onFontFamilyChange,
  cellBorderRadiusPercent = 0,
  onCellBorderRadiusPercentChange,
  linkDisplayMode,
  linkButtonLabel = '',
  linkButtonBg,
  linkButtonFg,
  linkButtonShape,
  onLinkDisplayModeChange,
  onLinkButtonLabelChange,
  onLinkButtonBgChange,
  onLinkButtonFgChange,
  onLinkButtonShapeChange,
  linkUrlValue = '',
  onLinkUrlChange,
  linkButtonSize,
  onLinkButtonSizeChange,
}) {
  const [mergeDropdownOpen, setMergeDropdownOpen] = useState(false);
  const mergeActions = { onMergeUp, onMergeDown, onMergeLeft, onMergeRight };
  const mergeCans = { canMergeUp, canMergeDown, canMergeLeft, canMergeRight };

  const handleMergeDirection = (opt) => {
    const fn = mergeActions[opt.onKey];
    if (fn) { fn(); onClose(); }
    setMergeDropdownOpen(false);
  };

  const canMerge = canMergeUp || canMergeDown || canMergeLeft || canMergeRight;
  const isText = currentType === 'text';
  const isLinkType = currentType === 'link';
  const isCalendarType = currentType === 'calendar';
  const linkMode = linkDisplayMode || LINK_DISPLAY_URL;
  const effectiveLinkBg = linkButtonBg || DEFAULT_LINK_BUTTON_BG;
  const effectiveLinkFg = linkButtonFg || DEFAULT_LINK_BUTTON_FG;
  const effectiveLinkShape = linkButtonShape || LINK_SHAPE_ROUNDED;
  const effectiveLinkButtonSize = linkButtonSize || DEFAULT_LINK_BUTTON_SIZE;

  return (
    <View style={styles.widgetPicker}>
      <View style={styles.widgetPickerContent}>
        {/* Type chips */}
        <View style={styles.widgetPickerTypeRow}>
          {(widgetTypes || []).map(t => (
            <TouchableOpacity
              key={t.id}
              onPress={() => { onSelect(cellId, t.id); onClose(); }}
              style={[
                styles.widgetPickerItem,
                currentType === t.id && [styles.widgetPickerItemActive, { backgroundColor: accentColor }],
              ]}
            >
              <Ionicons name={t.icon} size={18} color={currentType === t.id ? '#fff' : '#333'} />
              <Text style={[styles.widgetPickerLabel, currentType === t.id && styles.widgetPickerLabelActive]}>{t.label}</Text>
            </TouchableOpacity>
          ))}
        </View>

        {(isLinkType || isCalendarType) ? (
          <View style={styles.pickerBlockTop}>
            <Text style={[styles.pickerBlockTitle, { color: accentColor }]}>Cell</Text>
          </View>
        ) : null}

        {/* Cell background swatches */}
        <View style={styles.pickerSection}>
          <Text style={styles.pickerSectionLabel}>Cell background</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 4 }}>
            <View style={styles.swatchRow}>
              {CELL_BG_SWATCHES.map((color, i) => (
                <TouchableOpacity
                  key={i}
                  onPress={() => onCellBgChange(cellId, color)}
                  style={[
                    styles.swatch,
                    color ? { backgroundColor: color } : styles.swatchClear,
                    !color && { borderWidth: 1, borderColor: '#ccc' },
                    cellBg === color && color && { borderWidth: 2, borderColor: accentColor },
                    !cellBg && !color && { borderWidth: 2, borderColor: accentColor },
                  ]}
                >
                  {!color && <Text style={styles.swatchClearText}>✕</Text>}
                </TouchableOpacity>
              ))}
            </View>
          </ScrollView>
        </View>

        <View style={styles.pickerSection}>
          <Text style={styles.pickerSectionLabel}>Corner roundness</Text>
          <Text style={styles.radiusSliderHint}>0–100% (100% = half the shorter side)</Text>
          <View style={styles.radiusSliderHeader}>
            <Text style={styles.radiusSliderValue}>{Math.round(cellBorderRadiusPercent)}%</Text>
          </View>
          <Slider
            key={`radius-${cellId}`}
            style={styles.radiusSlider}
            minimumValue={0}
            maximumValue={100}
            step={1}
            value={typeof cellBorderRadiusPercent === 'number' && !Number.isNaN(cellBorderRadiusPercent) ? cellBorderRadiusPercent : 0}
            onValueChange={(v) => onCellBorderRadiusPercentChange(cellId, v)}
            minimumTrackTintColor={accentColor}
            maximumTrackTintColor="#ddd"
            thumbTintColor={accentColor}
          />
        </View>

        {/* Font size (text cells only) */}
        {isText && (
          <View style={styles.pickerSection}>
            <Text style={styles.pickerSectionLabel}>Font size</Text>
            <View style={styles.fontSizeRow}>
              {FONT_SIZE_OPTIONS.map(opt => (
                <TouchableOpacity
                  key={opt.id}
                  onPress={() => onFontSizeChange(cellId, opt.id)}
                  style={[
                    styles.fontSizeBtn,
                    fontSize === opt.id && { backgroundColor: accentColor, borderColor: accentColor },
                  ]}
                >
                  <Text style={[styles.fontSizeBtnText, fontSize === opt.id && { color: '#fff' }]}>
                    {opt.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        )}

        {isText && (
          <View style={styles.pickerSection}>
            <Text style={styles.pickerSectionLabel}>Font family</Text>
            <View style={styles.fontFamilyRow}>
              {FONT_FAMILY_OPTIONS.map((opt) => {
                const isSelectedFamily = (fontFamily || null) === opt.id;
                return (
                  <TouchableOpacity
                    key={opt.label}
                    onPress={() => onFontFamilyChange(cellId, opt.id)}
                    style={[
                      styles.fontFamilyBtn,
                      isSelectedFamily && { backgroundColor: accentColor, borderColor: accentColor },
                    ]}
                  >
                    <Text
                      style={[
                        styles.fontFamilyBtnText,
                        opt.id ? { fontFamily: opt.id } : undefined,
                        isSelectedFamily && { color: '#fff' },
                      ]}
                      numberOfLines={1}
                    >
                      {opt.label}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        )}

        {isText && (
          <View style={styles.pickerSection}>
            <Text style={styles.pickerSectionLabel}>Text align</Text>
            <View style={styles.textAlignRow}>
              {TEXT_ALIGN_OPTIONS.map((opt) => (
                <TouchableOpacity
                  key={opt.id}
                  onPress={() => onTextAlignChange(cellId, opt.id)}
                  style={[
                    styles.textAlignBtn,
                    (textAlign || 'center') === opt.id && { backgroundColor: accentColor, borderColor: accentColor },
                  ]}
                >
                  <Ionicons name={opt.icon} size={13} color={(textAlign || 'center') === opt.id ? '#fff' : '#333'} />
                  <Text style={[styles.textAlignBtnText, (textAlign || 'center') === opt.id && { color: '#fff' }]}>
                    {opt.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        )}

        {isText && (
          <View style={styles.pickerSection}>
            <Text style={styles.pickerSectionLabel}>Font color</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 4 }}>
              <View style={styles.swatchRow}>
                {FONT_COLOR_SWATCHES.map((color, i) => (
                  <TouchableOpacity
                    key={i}
                    onPress={() => onFontColorChange(cellId, color)}
                    style={[
                      styles.swatch,
                      color ? { backgroundColor: color } : styles.swatchClear,
                      !color && { borderWidth: 1, borderColor: '#ccc' },
                      textColor === color && color && { borderWidth: 2, borderColor: accentColor },
                      !textColor && !color && { borderWidth: 2, borderColor: accentColor },
                      color === '#ffffff' && { borderWidth: 1, borderColor: '#ddd' },
                    ]}
                  >
                    {!color && <Text style={styles.swatchClearText}>A</Text>}
                  </TouchableOpacity>
                ))}
              </View>
            </ScrollView>
          </View>
        )}

        {isLinkType && (
          <>
            <View style={styles.pickerBlockDivider} />
            <Text style={[styles.pickerBlockTitle, { color: accentColor }]}>Link</Text>
            <View style={styles.pickerSection}>
              <Text style={styles.pickerSectionLabel}>Font size</Text>
              <View style={styles.fontSizeRow}>
                {FONT_SIZE_OPTIONS.map((opt) => (
                  <TouchableOpacity
                    key={opt.id}
                    onPress={() => onFontSizeChange(cellId, opt.id)}
                    style={[
                      styles.fontSizeBtn,
                      fontSize === opt.id && { backgroundColor: accentColor, borderColor: accentColor },
                    ]}
                  >
                    <Text style={[styles.fontSizeBtnText, fontSize === opt.id && { color: '#fff' }]}>
                      {opt.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
            <View style={styles.pickerSection}>
              <Text style={styles.pickerSectionLabel}>Font family</Text>
              <View style={styles.fontFamilyRow}>
                {FONT_FAMILY_OPTIONS.map((opt) => {
                  const isSelectedFamily = (fontFamily || null) === opt.id;
                  return (
                    <TouchableOpacity
                      key={opt.label}
                      onPress={() => onFontFamilyChange(cellId, opt.id)}
                      style={[
                        styles.fontFamilyBtn,
                        isSelectedFamily && { backgroundColor: accentColor, borderColor: accentColor },
                      ]}
                    >
                      <Text
                        style={[
                          styles.fontFamilyBtnText,
                          opt.id ? { fontFamily: opt.id } : undefined,
                          isSelectedFamily && { color: '#fff' },
                        ]}
                        numberOfLines={1}
                      >
                        {opt.label}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>
            <View style={styles.pickerSection}>
              <Text style={styles.pickerSectionLabel}>
                {linkMode === LINK_DISPLAY_BUTTON ? 'URL text color (URL view)' : 'Link text color'}
              </Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 4 }}>
                <View style={styles.swatchRow}>
                  {FONT_COLOR_SWATCHES.map((color, i) => (
                    <TouchableOpacity
                      key={i}
                      onPress={() => onFontColorChange(cellId, color)}
                      style={[
                        styles.swatch,
                        color ? { backgroundColor: color } : styles.swatchClear,
                        !color && { borderWidth: 1, borderColor: '#ccc' },
                        textColor === color && color && { borderWidth: 2, borderColor: accentColor },
                        !textColor && !color && { borderWidth: 2, borderColor: accentColor },
                        color === '#ffffff' && { borderWidth: 1, borderColor: '#ddd' },
                      ]}
                    >
                      {!color && <Text style={styles.swatchClearText}>A</Text>}
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>
            <View style={styles.pickerSection}>
              <Text style={styles.pickerSectionLabel}>Show as</Text>
              <View style={styles.fontSizeRow}>
                {LINK_DISPLAY_MODE_PICKER.map((opt) => {
                  const selected = linkMode === opt.id;
                  return (
                    <TouchableOpacity
                      key={opt.id}
                      onPress={() => onLinkDisplayModeChange(cellId, opt.id)}
                      style={[
                        styles.fontSizeBtn,
                        selected && { backgroundColor: accentColor, borderColor: accentColor },
                      ]}
                    >
                      <Text style={[styles.fontSizeBtnText, selected && { color: '#fff' }]}>{opt.label}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>
            {linkMode === LINK_DISPLAY_BUTTON && (
              <>
                <View style={styles.pickerSection}>
                  <Text style={styles.pickerSectionLabel}>Link URL</Text>
                  <TextInput
                    style={[styles.linkModalUrlInput, { fontFamily: resolvePostGridFontFamily(fontFamily) }]}
                    value={linkUrlValue}
                    onChangeText={(t) => onLinkUrlChange(cellId, t)}
                    placeholder="https://..."
                    placeholderTextColor="#999"
                    keyboardType="url"
                    autoCapitalize="none"
                    autoCorrect={false}
                  />
                </View>
                <View style={styles.pickerSection}>
                  <Text style={styles.pickerSectionLabel}>Button text</Text>
                  <TextInput
                    style={[styles.linkButtonLabelInput, { fontFamily: resolvePostGridFontFamily(fontFamily) }]}
                    value={linkButtonLabel}
                    onChangeText={(t) => onLinkButtonLabelChange(cellId, t)}
                    placeholder="Open link"
                    placeholderTextColor="#999"
                  />
                </View>
                <View style={styles.pickerSection}>
                  <Text style={styles.pickerSectionLabel}>Button size</Text>
                  <View style={styles.fontSizeRow}>
                    {LINK_BUTTON_SIZE_PICKER.map((opt) => {
                      const selected = effectiveLinkButtonSize === opt.id;
                      return (
                        <TouchableOpacity
                          key={opt.id}
                          onPress={() => onLinkButtonSizeChange(cellId, opt.id)}
                          style={[
                            styles.fontSizeBtn,
                            selected && { backgroundColor: accentColor, borderColor: accentColor },
                          ]}
                        >
                          <Text style={[styles.fontSizeBtnText, selected && { color: '#fff' }]}>{opt.label}</Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                </View>
                <View style={styles.pickerSection}>
                  <Text style={styles.pickerSectionLabel}>Button color</Text>
                  <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 4 }}>
                    <View style={styles.swatchRow}>
                      {LINK_BUTTON_BG_SWATCHES.map((color, i) => (
                        <TouchableOpacity
                          key={i}
                          onPress={() => onLinkButtonBgChange(cellId, color)}
                          style={[
                            styles.swatch,
                            { backgroundColor: color },
                            color === '#ffffff' && { borderWidth: 1, borderColor: '#ccc' },
                            effectiveLinkBg === color && { borderWidth: 2, borderColor: accentColor },
                          ]}
                        />
                      ))}
                    </View>
                  </ScrollView>
                </View>
                <View style={styles.pickerSection}>
                  <Text style={styles.pickerSectionLabel}>Button text color</Text>
                  <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 4 }}>
                    <View style={styles.swatchRow}>
                      {LINK_BUTTON_FG_SWATCHES.map((color, i) => (
                        <TouchableOpacity
                          key={i}
                          onPress={() => onLinkButtonFgChange(cellId, color)}
                          style={[
                            styles.swatch,
                            { backgroundColor: color },
                            color === '#ffffff' && { borderWidth: 1, borderColor: '#ccc' },
                            effectiveLinkFg === color && { borderWidth: 2, borderColor: accentColor },
                          ]}
                        />
                      ))}
                    </View>
                  </ScrollView>
                </View>
                <View style={styles.pickerSection}>
                  <Text style={styles.pickerSectionLabel}>Button shape</Text>
                  <View style={styles.fontSizeRow}>
                    {LINK_SHAPE_PICKER.map((opt) => {
                      const selected = effectiveLinkShape === opt.id;
                      return (
                        <TouchableOpacity
                          key={opt.id}
                          onPress={() => onLinkButtonShapeChange(cellId, opt.id)}
                          style={[
                            styles.fontSizeBtn,
                            selected && { backgroundColor: accentColor, borderColor: accentColor },
                          ]}
                        >
                          <Text style={[styles.fontSizeBtnText, selected && { color: '#fff' }]}>{opt.label}</Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                </View>
              </>
            )}
          </>
        )}

        {isCalendarType && (
          <>
            <View style={styles.pickerBlockDivider} />
            <Text style={[styles.pickerBlockTitle, { color: accentColor }]}>Calendar</Text>
            <View style={styles.pickerSection}>
              <Text style={styles.pickerSectionLabel}>Font size</Text>
              <View style={styles.fontSizeRow}>
                {FONT_SIZE_OPTIONS.map((opt) => (
                  <TouchableOpacity
                    key={opt.id}
                    onPress={() => onFontSizeChange(cellId, opt.id)}
                    style={[
                      styles.fontSizeBtn,
                      fontSize === opt.id && { backgroundColor: accentColor, borderColor: accentColor },
                    ]}
                  >
                    <Text style={[styles.fontSizeBtnText, fontSize === opt.id && { color: '#fff' }]}>
                      {opt.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
            <View style={styles.pickerSection}>
              <Text style={styles.pickerSectionLabel}>Font family</Text>
              <View style={styles.fontFamilyRow}>
                {FONT_FAMILY_OPTIONS.map((opt) => {
                  const isSelectedFamily = (fontFamily || null) === opt.id;
                  return (
                    <TouchableOpacity
                      key={opt.label}
                      onPress={() => onFontFamilyChange(cellId, opt.id)}
                      style={[
                        styles.fontFamilyBtn,
                        isSelectedFamily && { backgroundColor: accentColor, borderColor: accentColor },
                      ]}
                    >
                      <Text
                        style={[
                          styles.fontFamilyBtnText,
                          opt.id ? { fontFamily: opt.id } : undefined,
                          isSelectedFamily && { color: '#fff' },
                        ]}
                        numberOfLines={1}
                      >
                        {opt.label}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>
            <View style={styles.pickerSection}>
              <Text style={styles.pickerSectionLabel}>Icon and date text color</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 4 }}>
                <View style={styles.swatchRow}>
                  {FONT_COLOR_SWATCHES.map((color, i) => (
                    <TouchableOpacity
                      key={i}
                      onPress={() => onFontColorChange(cellId, color)}
                      style={[
                        styles.swatch,
                        color ? { backgroundColor: color } : styles.swatchClear,
                        !color && { borderWidth: 1, borderColor: '#ccc' },
                        textColor === color && color && { borderWidth: 2, borderColor: accentColor },
                        !textColor && !color && { borderWidth: 2, borderColor: accentColor },
                        color === '#ffffff' && { borderWidth: 1, borderColor: '#ddd' },
                      ]}
                    >
                      {!color && <Text style={styles.swatchClearText}>A</Text>}
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>
          </>
        )}

        {/* Merge / Unmerge */}
        <View style={styles.widgetPickerMergeRow}>
          {canMerge && (
            <View style={styles.mergeDropdownWrap}>
              <Text style={styles.mergeDropdownLabel}>Merge:</Text>
              <View style={styles.mergeDropdownTriggerWrap}>
                <TouchableOpacity
                  onPress={() => setMergeDropdownOpen(prev => !prev)}
                  style={styles.mergeDropdownTrigger}
                  activeOpacity={0.7}
                >
                  <Text style={[styles.mergeDropdownTriggerText, { color: accentColor }]}>Select</Text>
                  <Ionicons name={mergeDropdownOpen ? 'chevron-up' : 'chevron-down'} size={16} color={accentColor} />
                </TouchableOpacity>
                {mergeDropdownOpen && (
                  <View style={styles.mergeDropdownList}>
                    {MERGE_OPTIONS.filter(opt => mergeCans[opt.canKey]).map(opt => (
                      <TouchableOpacity
                        key={opt.id}
                        onPress={() => handleMergeDirection(opt)}
                        style={styles.mergeDropdownItem}
                        activeOpacity={0.7}
                      >
                        <Ionicons name={opt.icon} size={16} color={accentColor} />
                        <Text style={[styles.widgetPickerMergeLabel, { color: accentColor }]}>{opt.label}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                )}
              </View>
            </View>
          )}
          {canUnmerge && (
            <TouchableOpacity onPress={() => onUnmerge(cellId)} style={styles.widgetPickerMergeBtn}>
              <Ionicons name="contract-outline" size={16} color={accentColor} />
              <Text style={[styles.widgetPickerMergeLabel, { color: accentColor }]}>Unmerge</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    </View>
  );
}

// ─── Draggable / Droppable Cell ──────────────────────────────────────────────

const MOVE_ANIM_DURATION = 220;

function DraggableDroppableGridCell({
  id,
  widget,
  colSpan = 1,
  colIndex = 0,
  rowSpan = 1,
  rowIndex = 0,
  widgetTypes,
  cellSize,
  accentColor,
  isSelected,
  isDraggable,
  onActivate,
  onTypeChange,
  onValueChange,
  onReportLayout,
  onSelectSingle,
  onFileUpload,
  onOpenCalendarModal,
  isUploading,
  isLastInRow,
  isLastRow,
  gridBgColor,
  animateFromPixels,
  interactionMode = 'move',
  onResizeHandleStart,
  rowHeightMultipliers,
}) {
  const cellRef = useRef(null);
  const { attributes, listeners, setNodeRef: setDraggableRef } = useDraggable({ id, data: { id } });
  const { setNodeRef: setDroppableRef } = useDroppable({ id, data: { id } });
  const setNodeRef = useCallback((node) => {
    setDraggableRef(node);
    setDroppableRef(node);
    cellRef.current = node;
  }, [setDraggableRef, setDroppableRef]);

  const translateX = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(0)).current;
  const lastAnimateKeyRef = useRef(null);

  useEffect(() => {
    if (!animateFromPixels) {
      lastAnimateKeyRef.current = null;
      translateX.setValue(0);
      translateY.setValue(0);
      return;
    }
    const key = `${animateFromPixels.translateX},${animateFromPixels.translateY}`;
    if (lastAnimateKeyRef.current === key) return;
    lastAnimateKeyRef.current = key;
    translateX.setValue(animateFromPixels.translateX);
    translateY.setValue(animateFromPixels.translateY);
    Animated.parallel([
      Animated.timing(translateX, { toValue: 0, duration: MOVE_ANIM_DURATION, useNativeDriver: true }),
      Animated.timing(translateY, { toValue: 0, duration: MOVE_ANIM_DURATION, useNativeDriver: true }),
    ]).start();
  }, [animateFromPixels, translateX, translateY]);

  const allowedIds = useMemo(() => (widgetTypes || []).map(t => t.id), [widgetTypes]);
  const showType = widget.type === EMPTY_WIDGET_TYPE || !widget.type
    ? EMPTY_WIDGET_TYPE
    : (allowedIds.includes(widget.type) ? widget.type : EMPTY_WIDGET_TYPE);

  const cellW = colSpan * cellSize;
  const cellH = cellPixelHeight(widget.y, rowSpan, cellSize, rowHeightMultipliers);
  const radiusPx = resolveCellBorderRadiusPx(widget, cellW, cellH);
  const isCompactMedia = colSpan === 1 && rowSpan === 1;

  const style = {
    width: cellW,
    minHeight: cellH,
  };

  const updateLayout = useCallback(() => {
    if (cellRef.current) {
      cellRef.current.measureInWindow((x, y, width, height) => {
        onReportLayout?.(id, { x, y, width, height });
      });
    }
  }, [id, onReportLayout]);

  const handleLongPress = useCallback(() => {
    updateLayout();
    if (cellRef.current) {
      cellRef.current.measureInWindow((x, y, width, height) => {
        onSelectSingle(id);
        onActivate(id, { x, y, width, height });
      });
    } else {
      onSelectSingle(id);
      onActivate(id, null);
    }
  }, [id, onSelectSingle, onActivate, updateLayout]);

  const handleFocus = useCallback(() => onSelectSingle(id), [id, onSelectSingle]);
  const handleLayout = useCallback(() => updateLayout(), [updateLayout]);

  const cellContent = (
    <View
      ref={setNodeRef}
      style={[
        styles.gridCell,
        gridBgColor ? { boxShadow: `0px 0px 0px 1px ${gridBgColor} inset` } : { boxShadow: '0px 0px 0px 1px rgba(255, 255, 255, 0.9) inset' },
        style,
        widget.cellBg ? { backgroundColor: widget.cellBg } : undefined,
        radiusPx != null && radiusPx > 0 ? { borderRadius: radiusPx, overflow: 'hidden' } : null,
        isSelected && [styles.gridCellSelected, { boxShadow: `0px 0px 0px 2px ${accentColor} inset` }],
      ]}
      onLayout={handleLayout}
    >
      <Pressable
        style={styles.gridCellInner}
        {...(isDraggable && interactionMode === 'move' ? { ...listeners, ...attributes } : {})}
        onPress={() => onSelectSingle(id)}
        onLongPress={handleLongPress}
      >
        <View style={styles.gridCellBody}>

          {/* EMPTY — tap plus to open widget picker */}
          {showType === EMPTY_WIDGET_TYPE && (
            <TouchableOpacity
              style={styles.gridEmptyCell}
              onPress={handleLongPress}
              activeOpacity={0.7}
            >
              <View style={[styles.gridEmptyPlusBtn, { borderColor: accentColor }]}>
                <Ionicons name="add" size={22} color={accentColor} />
              </View>
            </TouchableOpacity>
          )}

          {/* TEXT */}
          {showType === 'text' && (
            <TextInput
              style={[
                styles.gridInput,
                widget.fontSize === 'sm' && { fontSize: 13 },
                widget.fontSize === 'md' && { fontSize: 18 },
                widget.fontSize === 'lg' && { fontSize: 24 },
                widget.fontSize === 'xl' && { fontSize: 32 },
                widget.fontFamily ? { fontFamily: widget.fontFamily } : undefined,
                widget.textColor ? { color: widget.textColor } : undefined,
                { textAlign: widget.textAlign || 'center' },
                transparentTextInputStyle,
              ]}
              placeholder="Type..."
              value={widget.value}
              onChangeText={v => onValueChange(id, v)}
              onFocus={handleFocus}
              placeholderTextColor="#999"
              multiline
            />
          )}

          {/* FILE (image / video / audio) */}
          {showType === 'file' && (() => {
            let parsed = null;
            if (widget.value) {
              try { parsed = JSON.parse(widget.value); } catch { parsed = null; }
            }
            if (isUploading) {
              return (
                <View style={[styles.gridInputPlaceholder, styles.gridMediaFilled]}>
                  <ActivityIndicator size="small" color={accentColor} />
                </View>
              );
            }
            if (parsed?.url) {
              const isImage = (parsed.mimeType && parsed.mimeType.startsWith('image/')) ||
                /\.(jpe?g|png|gif|webp|bmp)$/i.test(parsed.name || '');
              const isVideo = (parsed.mimeType && parsed.mimeType.startsWith('video/')) ||
                /\.(mp4|webm|mov|avi|m4v)$/i.test(parsed.name || '');
              const isPdf = (parsed.mimeType === 'application/pdf') || /\.pdf$/i.test(parsed.name || '');
              const isAudio = !isVideo && ((parsed.mimeType && parsed.mimeType.startsWith('audio/')) ||
                /\.(mp3|wav|wave|ogg|aac|flac|m4a)$/i.test(parsed.name || ''));
              return (
                <View style={[styles.gridInputPlaceholder, styles.gridMediaFilled]}>
                  <TouchableOpacity style={styles.mediaRemoveBtn} onPress={() => onValueChange(id, '')}>
                    <Ionicons name="close-circle" size={18} color="rgba(0,0,0,0.6)" />
                  </TouchableOpacity>
                  {isImage && (
                    <Image source={{ uri: parsed.url }} style={styles.gridMediaImage} resizeMode="contain" />
                  )}
                  {isVideo && Platform.OS === 'web' ? (
                    // eslint-disable-next-line jsx-a11y/media-has-caption
                    <video
                      src={parsed.url}
                      playsInline
                      controls
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : isVideo ? (
                    <View style={styles.gridMediaVideoWrap}>
                      <Ionicons name="videocam" size={28} color="rgba(255,255,255,0.9)" />
                      <Text style={styles.gridMediaVideoLabel} numberOfLines={1}>{parsed.name || 'Video'}</Text>
                    </View>
                  ) : null}
                  {isPdf && (
                    <TouchableOpacity
                      style={styles.gridMediaVideoWrap}
                      onPress={() => { try { if (Platform.OS === 'web' && typeof window !== 'undefined') window.open(parsed.url, '_blank'); else Linking.openURL(parsed.url).catch(() => {}); } catch (_) {} }}
                      activeOpacity={0.8}
                    >
                      <Ionicons name="document-text" size={28} color="rgba(255,255,255,0.9)" />
                      <Text style={styles.gridMediaVideoLabel} numberOfLines={1}>{parsed.name || 'PDF'}</Text>
                    </TouchableOpacity>
                  )}
                  {isAudio && (
                    <View style={[styles.gridMediaFilledInner, isCompactMedia && styles.gridMediaFilledInnerCompact]}>
                      <CompactAudioPlayer
                        url={parsed.url}
                        name={parsed.name}
                        compact={isCompactMedia}
                        accentColor={accentColor}
                      />
                    </View>
                  )}
                  {!isImage && !isVideo && !isPdf && !isAudio && (
                    <View style={styles.gridMediaVideoWrap}>
                      <Ionicons name="document-outline" size={24} color="rgba(255,255,255,0.9)" />
                      <Text style={styles.gridMediaVideoLabel} numberOfLines={1}>{parsed.name || 'File'}</Text>
                    </View>
                  )}
                </View>
              );
            }
            return (
              <TouchableOpacity
                style={styles.gridInputPlaceholder}
                onPress={() => onFileUpload?.(id, 'file')}
                disabled={isUploading}
                activeOpacity={0.8}
              >
                <Ionicons name="image-outline" size={20} color="#999" />
                <Text style={styles.gridInputPlaceholderText} numberOfLines={1}>
                  Tap to upload image / video / audio / PDF
                </Text>
              </TouchableOpacity>
            );
          })()}

          {/* AUDIO */}
          {showType === 'audio' && (() => {
            if (isUploading) {
              return (
                <View style={[styles.gridInputPlaceholder, styles.gridMediaFilled]}>
                  <ActivityIndicator size="small" color={accentColor} />
                </View>
              );
            }
            let parsed = null;
            if (widget.value) {
              try { parsed = JSON.parse(widget.value); } catch { parsed = null; }
            }
            if (parsed?.url) {
              return (
                <View style={[styles.gridInputPlaceholder, styles.gridMediaFilled, styles.audioCellFilled]}>
                  <TouchableOpacity style={styles.mediaRemoveBtn} onPress={() => onValueChange(id, '')}>
                    <Ionicons name="close-circle" size={18} color="rgba(0,0,0,0.6)" />
                  </TouchableOpacity>
                  <CompactAudioPlayer
                    url={parsed.url}
                    name={parsed.name}
                    compact={isCompactMedia}
                    accentColor={accentColor}
                  />
                </View>
              );
            }
            return (
              <TouchableOpacity
                style={styles.gridInputPlaceholder}
                onPress={() => onFileUpload?.(id, 'audio')}
                disabled={isUploading}
                activeOpacity={0.8}
              >
                <Ionicons name="musical-notes-outline" size={20} color="#999" />
                <Text style={styles.gridInputPlaceholderText} numberOfLines={1}>Tap to upload audio</Text>
              </TouchableOpacity>
            );
          })()}

          {/* MAP */}
          {showType === 'map' && (
            <MapCellEdit
              value={widget.value || ''}
              onValueChange={onValueChange}
              id={id}
              accentColor={accentColor}
              gridBgColor={gridBgColor}
              colIndex={colIndex}
              rowIndex={rowIndex}
              isSelected={isSelected}
            />
          )}

          {/* LINK */}
          {showType === 'link' && (
            <View style={styles.linkCellEditWrap}>
              {getLinkDisplayMode(widget) === LINK_DISPLAY_URL ? (
                <>
                  <TextInput
                    style={[
                      styles.gridInput,
                      styles.linkCellUrlInput,
                      widget.fontSize === 'sm' && { fontSize: 13 },
                      widget.fontSize === 'md' && { fontSize: 18 },
                      widget.fontSize === 'lg' && { fontSize: 24 },
                      widget.fontSize === 'xl' && { fontSize: 32 },
                      { fontFamily: resolvePostGridFontFamily(widget.fontFamily) },
                      widget.textColor ? { color: widget.textColor } : undefined,
                    ]}
                    placeholder="https://..."
                    value={widget.value}
                    onChangeText={v => onValueChange(id, v)}
                    onFocus={handleFocus}
                    placeholderTextColor="#999"
                    keyboardType="url"
                  />
                  {!!(widget.value || '').trim() && (
                    <View style={styles.linkCellPreview}>
                      <Text
                        style={[
                          styles.linkCellPreviewUrl,
                          { fontFamily: resolvePostGridFontFamily(widget.fontFamily) },
                          widget.fontSize === 'sm' && { fontSize: 11 },
                          widget.fontSize === 'md' && { fontSize: 12 },
                          widget.fontSize === 'lg' && { fontSize: 13 },
                          widget.fontSize === 'xl' && { fontSize: 14 },
                          widget.textColor ? { color: widget.textColor } : undefined,
                        ]}
                        numberOfLines={1}
                      >
                        {widget.value}
                      </Text>
                    </View>
                  )}
                </>
              ) : (
                <View style={styles.linkCellButtonOnly}>
                  {(() => {
                    const m = getLinkButtonMetrics(getLinkButtonSize(widget), true);
                    return (
                      <View
                        style={[
                          styles.linkCellPreviewBtn,
                          {
                            backgroundColor: getLinkButtonBg(widget),
                            borderRadius: linkButtonShapeToBorderRadius(getLinkButtonShape(widget)),
                            paddingHorizontal: m.padH,
                            paddingVertical: m.padV,
                          },
                        ]}
                      >
                        <Text
                          style={[
                            styles.linkCellPreviewBtnText,
                            {
                              color: getLinkButtonFg(widget),
                              fontSize: m.fontSize,
                              fontFamily: resolvePostGridFontFamily(widget.fontFamily),
                            },
                          ]}
                          numberOfLines={2}
                        >
                          {getLinkButtonLabel(widget)}
                        </Text>
                      </View>
                    );
                  })()}
                  <Text style={styles.linkCellButtonHint} numberOfLines={2}>
                    Long press: paste URL and style in editor
                  </Text>
                </View>
              )}
            </View>
          )}

          {/* DATE */}
          {showType === 'calendar' && (() => {
            const parsed = parseCalendarValue(widget.value);
            const { startDate, endDate } = normalizeCalendarSelection(parsed);
            const displayDate = formatCalendarDisplayText(parsed);
            const calendarColor = widget.textColor || '#999';
            const openSystemCalendar = () => {
              if (!startDate) return;
              const title = encodeURIComponent('VRC Post Calendar Item');
              const startToken = startDate.replace(/-/g, '');
              const endToken = (endDate || startDate).replace(/-/g, '');
              const start = `${startToken}T120000Z`;
              const end = `${endToken}T130000Z`;
              const details = encodeURIComponent(displayDate || 'Calendar date');
              const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${start}/${end}&details=${details}`;
              Linking.openURL(url).catch(() => {});
            };
            return (
              <TouchableOpacity
                style={[styles.gridInputPlaceholder, {height: '100%'}]}
                onPress={() => onOpenCalendarModal?.(id)}
                activeOpacity={0.8}
              >
                <Ionicons name="calendar-outline" size={20} color={calendarColor} />
                <Text
                  style={[
                    styles.gridInputPlaceholderText,
                    widget.fontSize === 'sm' && { fontSize: 13 },
                    widget.fontSize === 'md' && { fontSize: 15 },
                    widget.fontSize === 'lg' && { fontSize: 18 },
                    widget.fontSize === 'xl' && { fontSize: 22 },
                    { fontFamily: resolvePostGridFontFamily(widget.fontFamily), color: calendarColor },
                    displayDate && styles.calendarFilledText,
                  ]}
                  numberOfLines={1}
                >
                  {displayDate || 'Set calendar date'}
                </Text>
                <View style={styles.calendarCellActions}>
                  {!!startDate && (
                    <TouchableOpacity
                      onPress={(e) => {
                        e?.stopPropagation?.();
                        onValueChange(id, '');
                      }}
                      style={styles.calendarCellActionBtn}
                    >
                      <Ionicons name="close-circle" size={16} color="#888" />
                    </TouchableOpacity>
                  )}
                  {!!startDate && (
                    <TouchableOpacity
                      onPress={(e) => {
                        e?.stopPropagation?.();
                        openSystemCalendar();
                      }}
                      style={styles.calendarCellActionBtn}
                    >
                      <Ionicons name="open-outline" size={16} color={accentColor} />
                    </TouchableOpacity>
                  )}
                </View>
              </TouchableOpacity>
            );
          })()}

          {/* MESSAGE */}
          {showType === 'message' && (
            <TouchableOpacity style={styles.gridInputPlaceholder} onPress={() => {}}>
              <Ionicons name="chatbubble-outline" size={20} color="#999" />
              <Text style={styles.gridInputPlaceholderText}>Message</Text>
              <Text style={styles.gridInputPlaceholderSubtext}>Start conversation</Text>
            </TouchableOpacity>
          )}
        </View>
      </Pressable>
      {/* Settings trigger — only visible on selected cells */}
      {isSelected && (
        <TouchableOpacity
          style={[styles.cellSettingsBtn, { borderColor: accentColor }]}
          onPress={handleLongPress}
          activeOpacity={0.7}
        >
          <Ionicons name="ellipsis-horizontal" size={12} color={accentColor} />
        </TouchableOpacity>
      )}
      {isSelected && interactionMode === 'resize' && (
        <>
          <TouchableOpacity
            style={[styles.resizeHandle, styles.resizeHandleTop]}
            onPressIn={(e) => onResizeHandleStart?.(id, 'up', e)}
          >
            <Ionicons name="arrow-up" size={10} color="#fff" />
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.resizeHandle, styles.resizeHandleBottom]}
            onPressIn={(e) => onResizeHandleStart?.(id, 'down', e)}
          >
            <Ionicons name="arrow-down" size={10} color="#fff" />
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.resizeHandle, styles.resizeHandleLeft]}
            onPressIn={(e) => onResizeHandleStart?.(id, 'left', e)}
          >
            <Ionicons name="arrow-back" size={10} color="#fff" />
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.resizeHandle, styles.resizeHandleRight]}
            onPressIn={(e) => onResizeHandleStart?.(id, 'right', e)}
          >
            <Ionicons name="arrow-forward" size={10} color="#fff" />
          </TouchableOpacity>
        </>
      )}
    </View>
  );

  return (
    <Animated.View
      style={[style, { transform: [{ translateX }, { translateY }], zIndex: isSelected ? 1000 : 0 }]}
      collapsable={false}
    >
      {cellContent}
    </Animated.View>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function EditablePostGrid({
  items,
  setItems,
  widgetTypes = [],
  onFileUpload,
  accentColor = '#1075ae',
  cellSize = 100,
  uploadingCellId = null,
  gridBgColor = '#fff',
  interactionMode = 'move',
}) {
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [previewLayout, setPreviewLayout] = useState(null);
  const [isDropInvalid, setIsDropInvalid] = useState(false);
  const [selectedCellIds, setSelectedCellIds] = useState([]);
  const [activeCellId, setActiveCellId] = useState(null);
  const [activeCellLayout, setActiveCellLayout] = useState(null);
  const [containerLayout, setContainerLayout] = useState(null);
  const [pickerHeight, setPickerHeight] = useState(240);
  const containerRef = useRef(null);
  const cellLayoutsRef = useRef({});
  const activeRectRef = useRef(null);
  const lastOverIdRef = useRef(null);
  const lastInvalidRef = useRef(false);
  const renderedLayoutRef = useRef(null);
  const previousLayoutRef = useRef(null);
  const [layoutVersion, setLayoutVersion] = useState(0);
  const [calendarModalCellId, setCalendarModalCellId] = useState(null);
  const [calendarModalMonth, setCalendarModalMonth] = useState(new Date());
  const [calendarRangeMode, setCalendarRangeMode] = useState(false);
  const [calendarPendingRangeStartIso, setCalendarPendingRangeStartIso] = useState('');
  const [resizeSession, setResizeSession] = useState(null); // { cellId, direction }

  const captureRenderedLayout = useCallback((layout) => {
    renderedLayoutRef.current = layout;
  }, []);

  const layoutToRender = activeId && previewLayout ? previewLayout : items;
  const grid = useMemo(() => buildGridFromItems(layoutToRender), [layoutToRender]);
  const rowHeightMultipliers = useMemo(() => getRowHeightMultipliers(), []);
  const rowTops = useMemo(() => {
    const tops = [0];
    for (let r = 0; r < GRID_ROWS; r++) {
      tops.push(tops[tops.length - 1] + cellSize * rowHeightMultipliers[r]);
    }
    return tops;
  }, [cellSize, rowHeightMultipliers]);

  useEffect(() => {
    captureRenderedLayout(layoutToRender);
  }, [layoutToRender, captureRenderedLayout]);

  useEffect(() => {
    if (!previousLayoutRef.current) return;
    const t = setTimeout(() => {
      previousLayoutRef.current = null;
      setLayoutVersion(v => v + 1);
    }, 250);
    return () => clearTimeout(t);
  }, [layoutToRender, layoutVersion]);

  useEffect(() => {
    if (!activeCellId || !activeCellLayout) {
      setContainerLayout(null);
      return;
    }
    containerRef.current?.measureInWindow((x, y) => setContainerLayout({ x, y }));
  }, [activeCellId, activeCellLayout]);

  const widgetIds = useMemo(() =>
    layoutToRender.slice().sort((a, b) => a.y !== b.y ? a.y - b.y : a.x - b.x).map(i => i.id),
  [layoutToRender]);

  const findCellAt = useCallback((pageX, pageY) => {
    const layouts = cellLayoutsRef.current;
    for (const id of Object.keys(layouts)) {
      const l = layouts[id];
      if (l && pageX >= l.x && pageX <= l.x + l.width && pageY >= l.y && pageY <= l.y + l.height) return id;
    }
    return null;
  }, []);

  const handleDragStart = useCallback((e) => {
    lastOverIdRef.current = null;
    lastInvalidRef.current = false;
    setActiveId(e.active.id);
    setOverId(null);
    setPreviewLayout(null);
    setIsDropInvalid(false);
    const rect = e.active.rect?.current;
    activeRectRef.current = rect
      ? { left: rect.left ?? rect.x ?? 0, top: rect.top ?? rect.y ?? 0, width: rect.width ?? 0, height: rect.height ?? 0 }
      : null;
  }, []);

  const handleDragOver = useCallback((event) => {
    const { active, over } = event;
    const currentItems = itemsRef.current;
    const activeIdFromEvent = active?.id;
    if (!over || !activeIdFromEvent) {
      if (lastOverIdRef.current !== null || lastInvalidRef.current) {
        lastOverIdRef.current = null;
        lastInvalidRef.current = false;
        setOverId(null);
        setPreviewLayout(null);
        setIsDropInvalid(false);
        previousLayoutRef.current = renderedLayoutRef.current?.map(i => ({ ...i })) ?? null;
      }
      return;
    }
    const targetId = over.id;
    if (targetId === activeIdFromEvent) {
      if (lastInvalidRef.current) { lastInvalidRef.current = false; setIsDropInvalid(false); }
      return;
    }
    const targetItem = currentItems.find(i => i.id === targetId);
    if (!targetItem) {
      if (lastOverIdRef.current !== targetId || !lastInvalidRef.current) {
        lastOverIdRef.current = targetId;
        lastInvalidRef.current = true;
        setOverId(targetId);
        setPreviewLayout(null);
        setIsDropInvalid(true);
        previousLayoutRef.current = renderedLayoutRef.current?.map(i => ({ ...i })) ?? null;
      }
      return;
    }
    const movingItem = currentItems.find(i => i.id === activeIdFromEvent);
    const movingW = movingItem?.w || 1;
    const movingH = movingItem?.h || 1;
    // Full-width items move by row: hovering any cell of another row band moves the whole band.
    if (movingW === GRID_COLS) {
      if (lastOverIdRef.current === targetId) return;
      lastOverIdRef.current = targetId;
      const rowLayout = moveRowBand(currentItems, activeIdFromEvent, targetItem.y);
      previousLayoutRef.current = renderedLayoutRef.current?.map(i => ({ ...i })) ?? null;
      setOverId(targetId);
      setPreviewLayout(rowLayout);
      lastInvalidRef.current = false;
      setIsDropInvalid(false);
      return;
    }
    const targetIsMerged = (targetItem.w || 1) > 1 || (targetItem.h || 1) > 1;
    if (targetIsMerged) {
      if (lastOverIdRef.current !== targetId || !lastInvalidRef.current) {
        lastOverIdRef.current = targetId;
        lastInvalidRef.current = true;
        setOverId(targetId);
        setPreviewLayout(null);
        setIsDropInvalid(true);
        previousLayoutRef.current = renderedLayoutRef.current?.map(i => ({ ...i })) ?? null;
      }
      return;
    }
    if (lastOverIdRef.current === targetId && !lastInvalidRef.current) return;
    lastOverIdRef.current = targetId;
    lastInvalidRef.current = false;
    // Pull the target left/up so a wide or tall item still fits when hovering its row's later cells.
    const targetX = Math.min(targetItem.x, GRID_COLS - movingW);
    const targetY = Math.min(targetItem.y, GRID_ROWS - movingH);
    const nextLayout = placeAndReflow(currentItems, activeIdFromEvent, targetX, targetY);
    if (nextLayout) {
      previousLayoutRef.current = renderedLayoutRef.current?.map(i => ({ ...i })) ?? null;
      setOverId(targetId);
      setPreviewLayout(nextLayout);
      setIsDropInvalid(false);
    } else {
      lastInvalidRef.current = true;
      setOverId(targetId);
      setPreviewLayout(null);
      setIsDropInvalid(true);
      previousLayoutRef.current = renderedLayoutRef.current?.map(i => ({ ...i })) ?? null;
    }
  }, []);

  const handleDragEnd = useCallback(() => {
    if (previewLayout && !isDropInvalid) {
      previousLayoutRef.current = renderedLayoutRef.current?.map(i => ({ ...i })) ?? null;
      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
      setItems(previewLayout);
    }
    lastOverIdRef.current = null;
    lastInvalidRef.current = false;
    setActiveId(null);
    setOverId(null);
    setPreviewLayout(null);
    setIsDropInvalid(false);
    activeRectRef.current = null;
  }, [previewLayout, isDropInvalid, setItems]);

  const closePicker = useCallback(() => {
    setActiveCellId(null);
    setActiveCellLayout(null);
  }, []);

  const handleActivateCell = useCallback((id, layout) => {
    setActiveCellId(id);
    setActiveCellLayout(layout || null);
  }, []);

  const handleTypeChange = useCallback((id, type) => {
    setItems(prev => prev.map((i) => {
      if (i.id !== id) return i;
      const next = { ...i, type };
      if (type !== 'link' && i.type === 'link') {
        next.linkDisplayMode = undefined;
        next.linkButtonLabel = undefined;
        next.linkButtonBg = undefined;
        next.linkButtonFg = undefined;
        next.linkButtonShape = undefined;
        next.linkButtonSize = undefined;
      }
      return next;
    }));
    closePicker();
  }, [closePicker, setItems]);

  const handleValueChange = useCallback((id, value) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, value } : i));
  }, [setItems]);

  const handleOpenCalendarModal = useCallback((id) => {
    const target = itemsRef.current.find((i) => i.id === id);
    const parsed = parseCalendarValue(target?.value);
    const { startDate, endDate } = normalizeCalendarSelection(parsed);
    const parsedDate = parseIsoDate(startDate || '');
    setCalendarModalMonth(parsedDate || new Date());
    setCalendarModalCellId(id);
    setCalendarRangeMode(!!endDate);
    setCalendarPendingRangeStartIso('');
    setActiveCellId(id);
  }, []);

  const handleCloseCalendarModal = useCallback(() => {
    setCalendarModalCellId(null);
    setCalendarPendingRangeStartIso('');
  }, []);

  const handleSelectCalendarDate = useCallback((isoDate) => {
    if (!calendarModalCellId) return;
    if (!calendarRangeMode) {
      setItems(prev => prev.map((i) => {
        if (i.id !== calendarModalCellId) return i;
        const nextValue = JSON.stringify({ date: isoDate, startDate: isoDate, endDate: '' });
        return { ...i, value: nextValue };
      }));
      setCalendarModalCellId(null);
      return;
    }
    if (!calendarPendingRangeStartIso) {
      setCalendarPendingRangeStartIso(isoDate);
      return;
    }
    const start = isoDate < calendarPendingRangeStartIso ? isoDate : calendarPendingRangeStartIso;
    const end = isoDate < calendarPendingRangeStartIso ? calendarPendingRangeStartIso : isoDate;
    setItems(prev => prev.map((i) => {
      if (i.id !== calendarModalCellId) return i;
      const nextValue = JSON.stringify({
        date: start,
        startDate: start,
        endDate: end,
      });
      return { ...i, value: nextValue };
    }));
    setCalendarPendingRangeStartIso('');
    setCalendarModalCellId(null);
  }, [calendarModalCellId, calendarPendingRangeStartIso, calendarRangeMode, setItems]);

  const handleClearCalendarDate = useCallback(() => {
    if (!calendarModalCellId) return;
    setItems(prev => prev.map((i) => (i.id === calendarModalCellId ? { ...i, value: '' } : i)));
    setCalendarPendingRangeStartIso('');
    setCalendarModalCellId(null);
  }, [calendarModalCellId, setItems]);

  const handleCellBgChange = useCallback((id, cellBg) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, cellBg: cellBg ?? undefined } : i));
  }, [setItems]);

  const handleCellBorderRadiusPercentChange = useCallback((id, percent) => {
    const p = typeof percent === 'number' && !Number.isNaN(percent) ? Math.max(0, Math.min(100, Math.round(percent))) : 0;
    setItems(prev => prev.map(i => (i.id === id ? {
      ...i,
      cellBorderRadiusPercent: p > 0 ? p : undefined,
      cellBorderRadius: undefined,
    } : i)));
  }, [setItems]);

  const handleFontSizeChange = useCallback((id, fontSize) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, fontSize: fontSize ?? undefined } : i));
  }, [setItems]);

  const handleFontColorChange = useCallback((id, textColor) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, textColor: textColor ?? undefined } : i));
  }, [setItems]);

  const handleTextAlignChange = useCallback((id, textAlign) => {
    const normalized = ['left', 'center', 'right'].includes(textAlign) ? textAlign : undefined;
    setItems(prev => prev.map(i => i.id === id ? { ...i, textAlign: normalized } : i));
  }, [setItems]);

  const handleFontFamilyChange = useCallback((id, fontFamily) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, fontFamily: fontFamily ?? undefined } : i));
  }, [setItems]);

  const handleLinkDisplayModeChange = useCallback((id, mode) => {
    setItems(prev => prev.map((i) => {
      if (i.id !== id) return i;
      if (mode === LINK_DISPLAY_BUTTON) {
        return {
          ...i,
          linkDisplayMode: LINK_DISPLAY_BUTTON,
          linkButtonBg: i.linkButtonBg || DEFAULT_LINK_BUTTON_BG,
          linkButtonFg: i.linkButtonFg || DEFAULT_LINK_BUTTON_FG,
          linkButtonShape: i.linkButtonShape || LINK_SHAPE_ROUNDED,
          linkButtonSize: i.linkButtonSize || DEFAULT_LINK_BUTTON_SIZE,
        };
      }
      return {
        ...i,
        linkDisplayMode: undefined,
        linkButtonLabel: undefined,
        linkButtonBg: undefined,
        linkButtonFg: undefined,
        linkButtonShape: undefined,
        linkButtonSize: undefined,
      };
    }));
  }, [setItems]);

  const handleLinkButtonLabelChange = useCallback((id, text) => {
    setItems(prev => prev.map(i => (i.id === id ? { ...i, linkButtonLabel: text } : i)));
  }, [setItems]);

  const handleLinkButtonBgChange = useCallback((id, color) => {
    setItems(prev => prev.map(i => (i.id === id ? { ...i, linkButtonBg: color } : i)));
  }, [setItems]);

  const handleLinkButtonFgChange = useCallback((id, color) => {
    setItems(prev => prev.map(i => (i.id === id ? { ...i, linkButtonFg: color } : i)));
  }, [setItems]);

  const handleLinkButtonShapeChange = useCallback((id, shape) => {
    setItems(prev => prev.map(i => (i.id === id ? { ...i, linkButtonShape: shape } : i)));
  }, [setItems]);

  const handleLinkButtonSizeChange = useCallback((id, size) => {
    setItems(prev => prev.map(i => (i.id === id ? { ...i, linkButtonSize: size } : i)));
  }, [setItems]);

  const onReportLayout = useCallback((id, layout) => {
    cellLayoutsRef.current[id] = layout;
  }, []);

  const onSelectSingle = useCallback((id) => setSelectedCellIds([id]), []);

  const itemsOverlappingRect = useCallback((itemsList, x, y, w, h) =>
    itemsList.filter(i => i.x < x + w && i.x + i.w > x && i.y < y + h && i.y + i.h > y),
  []);

  const applyResizeTowards = useCallback((cellId, direction, col, row) => {
    setItems((prev) => {
      const item = prev.find((i) => i.id === cellId);
      if (!item) return prev;
      const oldRect = { x: item.x, y: item.y, w: item.w, h: item.h };
      const oldRight = oldRect.x + oldRect.w;
      const oldBottom = oldRect.y + oldRect.h;
      let rect = { ...oldRect };

      if (direction === 'right') {
        const newRight = Math.max(oldRect.x + 1, Math.min(GRID_COLS, col + 1));
        rect = { ...rect, w: newRight - oldRect.x };
      } else if (direction === 'left') {
        const newLeft = Math.max(0, Math.min(oldRight - 1, col));
        rect = { ...rect, x: newLeft, w: oldRight - newLeft };
      } else if (direction === 'down') {
        const newBottom = Math.max(oldRect.y + 1, Math.min(GRID_ROWS, row + 1));
        rect = { ...rect, h: newBottom - oldRect.y };
      } else if (direction === 'up') {
        const newTop = Math.max(0, Math.min(oldBottom - 1, row));
        rect = { ...rect, y: newTop, h: oldBottom - newTop };
      } else {
        return prev;
      }

      if (rect.x < 0 || rect.y < 0 || rect.x + rect.w > GRID_COLS || rect.y + rect.h > GRID_ROWS) {
        return prev;
      }

      const overlapping = itemsOverlappingRect(prev, rect.x, rect.y, rect.w, rect.h);
      if (overlapping.length === 0) return prev;
      const overlapIds = new Set(overlapping.map((i) => i.id));
      const merged = { ...item, ...rect };
      const rest = prev.filter((i) => !overlapIds.has(i.id));

      // If user drags inward, recreate trimmed cells as empty widgets.
      const removedCells = [];
      for (let y = oldRect.y; y < oldRect.y + oldRect.h; y++) {
        for (let x = oldRect.x; x < oldRect.x + oldRect.w; x++) {
          const isInNewRect = x >= rect.x && x < rect.x + rect.w && y >= rect.y && y < rect.y + rect.h;
          if (!isInNewRect) removedCells.push({ x, y });
        }
      }
      let nextId = Math.max(0, ...prev.map((i) => {
        const n = parseInt(String(i.id).replace(/\D/g, '') || '0', 10);
        return Number.isNaN(n) ? 0 : n;
      })) + 1;
      const fillerCells = removedCells.map((c) => ({
        id: `cell-${nextId++}`,
        type: EMPTY_WIDGET_TYPE,
        value: '',
        x: c.x,
        y: c.y,
        w: 1,
        h: 1,
      }));

      return [merged, ...fillerCells, ...rest].sort((a, b) => (a.y !== b.y ? a.y - b.y : a.x - b.x));
    });
  }, [itemsOverlappingRect, setItems, widgetTypes]);

  const handleMergeDirection = useCallback((direction) => {
    if (!activeCellId) return;
    setItems(prev => {
      const item = prev.find(i => i.id === activeCellId);
      if (!item) return prev;
      let newRect = { x: item.x, y: item.y, w: item.w, h: item.h };
      if (direction === 'right' && item.x + item.w < GRID_COLS) {
        newRect = { x: item.x, y: item.y, w: item.w + 1, h: item.h };
      } else if (direction === 'left' && item.x > 0) {
        newRect = { x: item.x - 1, y: item.y, w: item.w + 1, h: item.h };
      } else if (direction === 'down' && item.y + item.h < GRID_ROWS) {
        newRect = { x: item.x, y: item.y, w: item.w, h: item.h + 1 };
      } else if (direction === 'up' && item.y > 0) {
        newRect = { x: item.x, y: item.y - 1, w: item.w, h: item.h + 1 };
      } else return prev;
      const overlapping = itemsOverlappingRect(prev, newRect.x, newRect.y, newRect.w, newRect.h);
      if (overlapping.length === 0) return prev;
      const mergedItem = { ...item, x: newRect.x, y: newRect.y, w: newRect.w, h: newRect.h };
      const overlapIds = new Set(overlapping.map(i => i.id));
      const rest = prev.filter(i => !overlapIds.has(i.id));
      return [mergedItem, ...rest].sort((a, b) => a.y !== b.y ? a.y - b.y : a.x - b.x);
    });
  }, [activeCellId, itemsOverlappingRect, setItems]);

  const handleResizeHandleStart = useCallback((cellId, direction) => {
    if (interactionMode !== 'resize') return;
    setResizeSession({ cellId, direction });
  }, [interactionMode]);

  useEffect(() => {
    if (!resizeSession || Platform.OS !== 'web' || typeof window === 'undefined') return;
    const onMove = (ev) => {
      containerRef.current?.measureInWindow((x, y) => {
        const relX = ev.pageX - x;
        const relY = ev.pageY - y;
        if (relX < 0 || relY < 0) return;
        const col = Math.max(0, Math.min(GRID_COLS - 1, Math.floor(relX / cellSize)));
        let row = 0;
        for (let r = 0; r < GRID_ROWS; r++) {
          if (relY >= rowTops[r] && relY < rowTops[r + 1]) {
            row = r;
            break;
          }
          if (relY >= rowTops[GRID_ROWS]) row = GRID_ROWS - 1;
        }
        applyResizeTowards(resizeSession.cellId, resizeSession.direction, col, row);
      });
    };
    const onUp = () => setResizeSession(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [resizeSession, applyResizeTowards, cellSize, rowTops]);

  const activeCellItem = activeCellId ? items.find(i => i.id === activeCellId) : null;
  const pickerCellId = activeCellItem?.id ?? null;
  const pickerCurrentType = activeCellItem?.type ?? 'text';
  const pickerCellBg = activeCellItem?.cellBg ?? null;
  const pickerFontSize = activeCellItem?.fontSize ?? null;
  const pickerFontFamily = activeCellItem?.fontFamily ?? null;
  const pickerTextAlign = activeCellItem?.textAlign ?? null;
  const pickerTextColor = activeCellItem?.textColor ?? null;
  const pickerActiveCellW = (activeCellItem?.w ?? 1) * cellSize;
  const pickerActiveCellH = activeCellItem
    ? cellPixelHeight(activeCellItem.y, activeCellItem.h ?? 1, cellSize, rowHeightMultipliers)
    : cellSize;
  const pickerCellBorderRadiusPercent = (() => {
    if (!activeCellItem) return 0;
    const pct = activeCellItem.cellBorderRadiusPercent;
    if (typeof pct === 'number' && !Number.isNaN(pct)) {
      return Math.max(0, Math.min(100, pct));
    }
    const legacy = activeCellItem.cellBorderRadius;
    if (typeof legacy === 'number' && legacy > 0 && pickerActiveCellW > 0 && pickerActiveCellH > 0) {
      const maxR = Math.min(pickerActiveCellW, pickerActiveCellH) * 0.5;
      if (maxR <= 0) return 0;
      return Math.min(100, Math.round((legacy / maxR) * 100));
    }
    return 0;
  })();
  const pickerLinkDisplayMode = activeCellItem?.type === 'link' ? getLinkDisplayMode(activeCellItem) : LINK_DISPLAY_URL;
  const pickerLinkButtonLabel = activeCellItem?.type === 'link' ? (activeCellItem.linkButtonLabel ?? '') : '';
  const pickerLinkButtonBg = activeCellItem?.type === 'link' ? activeCellItem.linkButtonBg : undefined;
  const pickerLinkButtonFg = activeCellItem?.type === 'link' ? activeCellItem.linkButtonFg : undefined;
  const pickerLinkButtonShape = activeCellItem?.type === 'link' ? activeCellItem.linkButtonShape : undefined;
  const pickerLinkUrlValue = activeCellItem?.type === 'link' ? (activeCellItem.value ?? '') : '';
  const pickerLinkButtonSize = activeCellItem?.type === 'link' ? activeCellItem.linkButtonSize : undefined;
  const canMergeLeft = !!(activeCellItem && activeCellItem.x > 0);
  const canMergeRight = !!(activeCellItem && activeCellItem.x + activeCellItem.w < GRID_COLS);
  const canMergeUp = !!(activeCellItem && activeCellItem.y > 0);
  const canMergeDown = !!(activeCellItem && activeCellItem.y + activeCellItem.h < GRID_ROWS);
  const canUnmergeActive = !!(activeCellItem && (activeCellItem.w > 1 || activeCellItem.h > 1));

  const handleUnmerge = useCallback((id) => {
    setItems(prev => {
      const item = prev.find(i => i.id === id);
      if (!item || (item.w === 1 && item.h === 1)) return prev;
      const result = prev.filter(i => i.id !== id);
      let nextId = Math.max(0, ...prev.map(i => {
        const n = parseInt(String(i.id).replace(/\D/g, '') || '0', 10);
        return isNaN(n) ? 0 : n;
      })) + 1;
      for (let row = item.y; row < item.y + item.h; row++) {
        for (let col = item.x; col < item.x + item.w; col++) {
          result.push({
            id: col === item.x && row === item.y ? id : `cell-${nextId++}`,
            type: col === item.x && row === item.y ? item.type : EMPTY_WIDGET_TYPE,
            value: col === item.x && row === item.y ? item.value : '',
            x: col, y: row, w: 1, h: 1,
          });
        }
      }
      return result;
    });
    closePicker();
  }, [closePicker, setItems, widgetTypes]);

  const activeItem = activeId ? items.find(i => i.id === activeId) : null;
  const firstTypeLabel = widgetTypes.find(t => t.id === activeItem?.type)?.label || widgetTypes[0]?.label || '—';

  return (
    <View ref={containerRef} style={styles.gridContainer} collapsable={false}>
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <View style={[styles.gridOuter, { width: GRID_COLS * cellSize, backgroundColor: gridBgColor }]}>
          {Array.from({ length: GRID_ROWS }, (_, rowIndex) => {
            const isActiveRow = selectedCellIds.length > 0 && items.find(i => selectedCellIds[0] === i.id)?.y === rowIndex;
            return (
            <View
              key={rowIndex}
              style={[
                styles.gridRow,
                { height: cellSize * rowHeightMultipliers[rowIndex] },
                isActiveRow ? styles.gridRowActive : null,
                rowIndex === GRID_ROWS - 1 && styles.gridRowLast,
              ]}
            >
              {Array.from({ length: GRID_COLS }, (_, colIndex) => {
                const cell = grid[rowIndex][colIndex];
                if (cell?.type === 'covered') {
                  const isSameRowAsStart = rowIndex === cell.item.y;
                  if (isSameRowAsStart) return null;
                  return (
                    <View
                      key={`covered-${rowIndex}-${colIndex}`}
                      style={[
                        styles.gridCellEmpty,
                        styles.gridCellCoveredPlaceholder,
                        { width: cellSize, height: cellSize * rowHeightMultipliers[rowIndex] },
                        colIndex === GRID_COLS - 1 && styles.gridCellLastInRow,
                        rowIndex === GRID_ROWS - 1 && styles.gridCellLastRow,
                      ]}
                    />
                  );
                }
                if (!cell) {
                  return (
                    <View
                      key={`empty-${rowIndex}-${colIndex}`}
                      style={[
                        styles.gridCellEmpty,
                        { width: cellSize, height: cellSize * rowHeightMultipliers[rowIndex] },
                        colIndex === GRID_COLS - 1 && styles.gridCellLastInRow,
                        rowIndex === GRID_ROWS - 1 && styles.gridCellLastRow,
                      ]}
                    />
                  );
                }
                const { item } = cell;
                const colSpan = item.w, rowSpan = item.h;
                const isSelected = selectedCellIds.includes(item.id);
                const prev = previousLayoutRef.current?.find(i => i.id === item.id);
                const animateFromPixels =
                  prev && (prev.x !== item.x || prev.y !== item.y)
                    ? {
                        translateX: (prev.x - item.x) * cellSize,
                        translateY: (rowTops[prev.y] ?? 0) - (rowTops[item.y] ?? 0),
                      }
                    : null;
                return (
                  <DraggableDroppableGridCell
                    key={item.id}
                    id={item.id}
                    widget={item}
                    colSpan={colSpan}
                    colIndex={colIndex}
                    rowIndex={rowIndex}
                    rowSpan={rowSpan}
                    widgetTypes={widgetTypes}
                    cellSize={cellSize}
                    accentColor={accentColor}
                    isSelected={isSelected}
                    isDraggable={interactionMode === 'move'}
                    onActivate={handleActivateCell}
                    onTypeChange={handleTypeChange}
                    onValueChange={handleValueChange}
                    onReportLayout={onReportLayout}
                    onSelectSingle={onSelectSingle}
                    onFileUpload={onFileUpload}
                    onOpenCalendarModal={handleOpenCalendarModal}
                    isUploading={uploadingCellId === item.id}
                    isLastInRow={colIndex + colSpan >= GRID_COLS}
                    isLastRow={rowIndex === GRID_ROWS - 1}
                    gridBgColor={gridBgColor}
                    animateFromPixels={animateFromPixels}
                    interactionMode={interactionMode}
                    onResizeHandleStart={handleResizeHandleStart}
                    rowHeightMultipliers={rowHeightMultipliers}
                  />
                );
              })}
            </View>
            );
          })}
        </View>

        <DragOverlay modifiers={[snapCenterToCursor]} dropAnimation={null}>
          {activeItem ? (
            <View
              style={[
                styles.gridCell,
                styles.gridCellOverlay,
                { width: (activeItem.w ?? 1) * cellSize, minHeight: (activeItem.h ?? 1) * cellSize },
                isDropInvalid && styles.gridCellOverlayInvalid,
              ]}
            >
              <View style={styles.gridCellBody}>
                <Text style={styles.gridCellBodyText} numberOfLines={2}>
                  {activeItem.value || firstTypeLabel}
                </Text>
              </View>
            </View>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Picker overlay */}
      <View
        style={[
          styles.pickerOverlay,
          (!activeCellId || !activeCellLayout || !containerLayout) && styles.pickerOverlayHidden,
        ]}
        pointerEvents={activeCellId && activeCellLayout && containerLayout ? 'box-none' : 'none'}
      >
        <Pressable style={StyleSheet.absoluteFillObject} onPress={closePicker} />
        <Pressable
          style={[
            styles.pickerPositionWrap,
            activeCellLayout && containerLayout && (() => {
              const cellRelY = activeCellLayout.y - containerLayout.y;
              const cellH = activeCellLayout.height || cellSize;
              const showAbove = cellRelY >= pickerHeight + 12;
              return {
                left: Math.max(4, activeCellLayout.x - containerLayout.x - cellSize / 2),
                top: showAbove
                  ? cellRelY - pickerHeight - 8
                  : cellRelY + cellH + 8,
              };
            })(),
          ]}
          onPress={() => {}}
          onLayout={e => setPickerHeight(e.nativeEvent.layout.height)}
        >
          <WidgetTypePicker
            cellId={pickerCellId}
            currentType={pickerCurrentType}
            widgetTypes={widgetTypes}
            accentColor={accentColor}
            cellBg={pickerCellBg}
            fontSize={pickerFontSize}
            fontFamily={pickerFontFamily}
            textAlign={pickerTextAlign}
            textColor={pickerTextColor}
            onSelect={handleTypeChange}
            onClose={closePicker}
            onUnmerge={handleUnmerge}
            canUnmerge={canUnmergeActive}
            onMergeLeft={() => handleMergeDirection('left')}
            onMergeRight={() => handleMergeDirection('right')}
            onMergeUp={() => handleMergeDirection('up')}
            onMergeDown={() => handleMergeDirection('down')}
            canMergeLeft={canMergeLeft}
            canMergeRight={canMergeRight}
            canMergeUp={canMergeUp}
            canMergeDown={canMergeDown}
            onCellBgChange={handleCellBgChange}
            onFontSizeChange={handleFontSizeChange}
            onTextAlignChange={handleTextAlignChange}
            onFontColorChange={handleFontColorChange}
            onFontFamilyChange={handleFontFamilyChange}
            cellBorderRadiusPercent={pickerCellBorderRadiusPercent}
            onCellBorderRadiusPercentChange={handleCellBorderRadiusPercentChange}
            linkDisplayMode={pickerLinkDisplayMode}
            linkButtonLabel={pickerLinkButtonLabel}
            linkButtonBg={pickerLinkButtonBg}
            linkButtonFg={pickerLinkButtonFg}
            linkButtonShape={pickerLinkButtonShape}
            onLinkDisplayModeChange={handleLinkDisplayModeChange}
            onLinkButtonLabelChange={handleLinkButtonLabelChange}
            onLinkButtonBgChange={handleLinkButtonBgChange}
            onLinkButtonFgChange={handleLinkButtonFgChange}
            onLinkButtonShapeChange={handleLinkButtonShapeChange}
            linkUrlValue={pickerLinkUrlValue}
            onLinkUrlChange={handleValueChange}
            linkButtonSize={pickerLinkButtonSize}
            onLinkButtonSizeChange={handleLinkButtonSizeChange}
          />
        </Pressable>
      </View>

      <CalendarPickerModal
        visible={!!calendarModalCellId}
        monthDate={calendarModalMonth}
        startIsoDate={normalizeCalendarSelection(parseCalendarValue(items.find((i) => i.id === calendarModalCellId)?.value)).startDate}
        endIsoDate={normalizeCalendarSelection(parseCalendarValue(items.find((i) => i.id === calendarModalCellId)?.value)).endDate}
        pendingRangeStartIso={calendarPendingRangeStartIso}
        rangeMode={calendarRangeMode}
        accentColor={accentColor}
        onPrevMonth={() => setCalendarModalMonth((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))}
        onNextMonth={() => setCalendarModalMonth((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))}
        onToggleRangeMode={() => {
          setCalendarRangeMode((prev) => !prev);
          setCalendarPendingRangeStartIso('');
        }}
        onClose={handleCloseCalendarModal}
        onSelectDate={handleSelectCalendarDate}
        onClearDate={handleClearCalendarDate}
      />
    </View>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  gridContainer: { position: 'relative' },
  gridOuter: {
    borderWidth: 1,
    borderColor: 'transparent',
    borderRadius: 4,
    backgroundColor: '#fff',
    alignSelf: 'center',
  },
  gridRow: { flexDirection: 'row' },
  gridRowActive: { position: 'relative', zIndex: 1000, elevation: 10 },
  gridRowLast: { borderBottomWidth: 0 },
  cellSettingsBtn: {
    position: 'absolute',
    bottom: 3,
    right: 3,
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: 'rgba(255,255,255,0.92)',
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },
  resizeHandle: {
    position: 'absolute',
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: 'rgba(0,0,0,0.7)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 11,
  },
  resizeHandleTop: {
    top: -9,
    left: '50%',
    marginLeft: -9,
  },
  resizeHandleBottom: {
    bottom: -9,
    left: '50%',
    marginLeft: -9,
  },
  resizeHandleLeft: {
    left: -9,
    top: '50%',
    marginTop: -9,
  },
  resizeHandleRight: {
    right: -9,
    top: '50%',
    marginTop: -9,
  },
  gridCell: {
    minHeight: 0,
    backgroundColor: 'rgba(200, 200, 200, 0.1)',
    position: 'relative',
    boxShadow: '0px 0px 0px 1px rgba(255, 255, 255, 0.9) inset',
  },
  gridCellSelected: {},
  gridCellLastInRow: { borderRightWidth: 0 },
  gridCellLastRow: { borderBottomWidth: 0 },
  gridCellEmpty: {},
  gridCellCoveredPlaceholder: { backgroundColor: 'transparent' },
  gridCellOverlay: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 6,
    elevation: 6,
    padding: 8,
  },
  gridCellOverlayInvalid: {
    backgroundColor: 'rgba(220, 53, 69, 0.25)',
    borderWidth: 2,
    borderColor: '#dc3545',
  },
  gridCellInner: { flex: 1, minHeight: 0, padding: 12, position: 'relative' },
  gridCellBody: { flex: 1, minHeight: 32, justifyContent: 'center' },
  gridEmptyCell: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 32,
  },
  gridEmptyPlusBtn: {
    width: 44,
    height: 44,
    borderRadius: 22,
    borderWidth: 1,
    borderStyle: 'dashed',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.65)',
  },
  gridInput: {
    fontFamily: 'Computer Says No',
    fontSize: getResponsiveValue({ mobile: 18, tablet: 20, laptop: 24, desktop: 25 }),
    fontWeight: '300',
    color: '#000',
    textAlign: 'center',
    textAlignVertical: 'center',
    padding: 4,
    borderRadius: 4,
    height: '90%',
    minHeight: 28,
    outlineWidth: 0,
    ...transparentTextInputStyle,
  },
  linkCellEditWrap: { flex: 1, width: '100%', minHeight: 0, alignSelf: 'stretch' },
  linkCellUrlInput: { height: undefined, minHeight: 28, maxHeight: 80, ...transparentTextInputStyle },
  linkCellPreview: { marginTop: 6, alignItems: 'center', width: '100%' },
  linkCellPreviewBtn: { paddingHorizontal: 10, paddingVertical: 4, maxWidth: '100%' },
  linkCellPreviewBtnText: { fontFamily: 'VastagoGrotesk', fontSize: 10, fontWeight: '600', textAlign: 'center' },
  linkCellPreviewUrl: { fontFamily: 'VastagoGrotesk', fontSize: 10, color: '#2ecc71', textDecorationLine: 'underline', textAlign: 'center' },
  linkButtonLabelInput: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 8,
    fontFamily: 'VastagoGrotesk',
    fontSize: 12,
    color: '#000',
    marginTop: 4,
    ...transparentTextInputStyle,
  },
  linkModalUrlInput: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 8,
    fontFamily: 'VastagoGrotesk',
    fontSize: 12,
    color: '#000',
    marginTop: 4,
    minHeight: 44,
    ...transparentTextInputStyle,
  },
  linkCellButtonOnly: { flex: 1, width: '100%', alignItems: 'center', justifyContent: 'center', paddingVertical: 4 },
  linkCellButtonHint: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 9,
    color: '#999',
    textAlign: 'center',
    marginTop: 6,
    paddingHorizontal: 4,
  },
  gridInputPlaceholder: {
    minHeight: 28,
    borderWidth: 1,
    borderColor: '#eee',
    borderRadius: 4,
    borderStyle: 'dashed',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  gridMediaFilled: { borderStyle: 'solid', padding: 0, flex: 1, alignSelf: 'stretch' },
  mediaRemoveBtn: {
    position: 'absolute',
    top: 4,
    right: 4,
    zIndex: 5,
    backgroundColor: 'rgba(255,255,255,0.8)',
    borderRadius: 10,
    padding: 2,
  },
  gridMediaImage: { width: '100%', height: '100%', minHeight: 40 },
  gridMediaVideoWrap: {
    flex: 1,
    width: '100%',
    minHeight: 40,
    backgroundColor: '#333',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 4,
  },
  gridMediaVideoLabel: { fontFamily: 'VastagoGrotesk', fontSize: 9, color: 'rgba(255,255,255,0.9)', marginTop: 2 },
  audioCellFilled: { backgroundColor: 'transparent', padding: 8 },
  gridMediaFilledInner: {
    flex: 1,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 6,
  },
  gridMediaFilledInnerCompact: {
    padding: 4,
  },
  gridInputPlaceholderText: { fontFamily: 'VastagoGrotesk', fontSize: 14, color: '#999', marginTop: 4 },
  gridInputPlaceholderSubtext: { fontFamily: 'VastagoGrotesk', fontSize: 9, color: '#bbb', marginTop: 1 },
  calendarCellActions: {
    position: 'absolute',
    right: 4,
    top: 4,
    flexDirection: 'row',
    gap: 4,
  },
  calendarCellFilled: {
    borderStyle: 'solid',
    borderColor: 'rgba(255,255,255,0.35)',
    backgroundColor: '#4e7de0',
  },
  calendarFilledText: {
    fontWeight: '700',
  },
  calendarCellActionBtn: {
    backgroundColor: 'rgba(255,255,255,0.8)',
    borderRadius: 10,
    padding: 2,
  },
  gridCellBodyText: { fontFamily: 'VastagoGrotesk', fontSize: 11, color: '#333' },
  calendarModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.35)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  calendarModalCard: {
    width: '100%',
    maxWidth: 360,
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 12,
  },
  calendarModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  calendarNavBtn: {
    padding: 6,
    borderRadius: 8,
    backgroundColor: 'rgba(16,117,174,0.08)',
  },
  calendarModalTitle: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 15,
    color: '#222',
    fontWeight: '600',
  },
  calendarWeekRow: {
    flexDirection: 'row',
    marginBottom: 6,
  },
  calendarWeekLabel: {
    width: `${100 / 7}%`,
    textAlign: 'center',
    fontFamily: 'VastagoGrotesk',
    fontSize: 11,
    color: '#666',
  },
  calendarGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  calendarDayCell: {
    width: `${100 / 7}%`,
    alignItems: 'center',
    justifyContent: 'center',
    height: 36,
  },
  calendarDayButton: {
    borderRadius: 8,
  },
  calendarDayText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 12,
    color: '#333',
  },
  calendarDayTextSelected: {
    color: '#fff',
    fontWeight: '700',
  },
  calendarRangeControls: {
    marginTop: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  calendarRangeToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderWidth: 1,
    borderColor: 'rgba(16,117,174,0.35)',
    borderRadius: 8,
    paddingVertical: 6,
    paddingHorizontal: 10,
    backgroundColor: 'rgba(16,117,174,0.08)',
  },
  calendarRangeToggleText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 12,
    color: '#1075ae',
  },
  calendarPendingText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 12,
    color: '#666',
  },
  calendarModalFooter: {
    marginTop: 8,
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  calendarFooterBtn: {
    paddingVertical: 8,
    paddingHorizontal: 10,
  },
  calendarFooterText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 13,
    color: '#555',
  },
  // Map cell
  mapCellRoot: {
    flex: 1,
    minHeight: 0,
    alignSelf: 'stretch',
    position: 'relative',
  },
  mapModeToggle: {
    flexDirection: 'row',
    gap: 4,
    marginBottom: 4,
  },
  mapModeBtn: {
    flex: 1,
    paddingVertical: 4,
    paddingHorizontal: 4,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#f5f5f5',
    alignItems: 'center',
  },
  mapModeBtnActive: {
    backgroundColor: '#3ba378',
    borderColor: '#3ba378',
  },
  mapModeBtnText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 8,
    fontWeight: '700',
    color: '#666',
    textAlign: 'center',
  },
  mapModeBtnTextActive: {
    color: '#fff',
  },
  mapCellFilled: {
    flex: 1,
    position: 'relative',
    minHeight: 0,
    overflow: 'hidden',
  },
  mapInputWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    padding: 4,
    borderWidth: 1,
    borderColor: '#eee',
    borderRadius: 4,
    minHeight: 32,
    maxHeight: 36,
    flexShrink: 0,
  },
  mapInput: {
    flex: 1,
    minWidth: 0,
    fontFamily: 'VastagoGrotesk',
    fontSize: 10,
    color: '#333',
    padding: 2,
    outlineWidth: 0,
  },
  mapSearchBtn: { padding: 4 },
  mapResultsDropdown: {
    position: 'absolute',
    top: 62,
    left: 0,
    width: '150%',
    right: 0,
    zIndex: 1000,
    elevation: 10,
    maxHeight: 200,
    backgroundColor: 'rgba(255,255,255,0.98)',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#ddd',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 6,
  },
  mapEmptyResultsText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 10,
    color: '#888',
    padding: 10,
    textAlign: 'center',
  },
  mapResultsScroll: {
    maxHeight: 196,
    backgroundColor: 'rgba(255,255,255,0.98)',
    borderRadius: 6,
  },
  mapResultsContent: {
    paddingBottom: 4,
  },
  mapResultItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 8,
    marginBottom: 4,
    backgroundColor: '#f5f5f5',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#eee',
  },
  mapResultText: {
    flex: 1,
    fontFamily: 'VastagoGrotesk',
    fontSize: 10,
    color: '#333',
  },
  mapPreviewImage: { width: '100%', height: '100%', minHeight: 40 },
  mapClearBtn: {
    position: 'absolute',
    top: 4,
    right: 4,
    zIndex: 5,
    backgroundColor: 'rgba(255,255,255,0.8)',
    borderRadius: 10,
    padding: 2,
  },
  // Picker
  pickerOverlay: { ...StyleSheet.absoluteFillObject, zIndex: 1000 },
  pickerOverlayHidden: { opacity: 0, pointerEvents: 'none' },
  pickerPositionWrap: { position: 'absolute', alignItems: 'center' },
  widgetPicker: { alignSelf: 'center' },
  widgetPickerContent: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 10,
    borderWidth: 1,
    borderColor: '#ccc',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.14,
    shadowRadius: 8,
    elevation: 6,
    minWidth: 240,
    maxWidth: 360,
  },
  widgetPickerTypeRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
    marginBottom: 8,
  },
  widgetPickerItem: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 6,
    backgroundColor: '#f5f5f5',
  },
  widgetPickerItemActive: {},
  widgetPickerLabel: { fontFamily: 'VastagoGrotesk', fontSize: 8, color: '#333' },
  widgetPickerLabelActive: { color: '#fff' },
  // Picker sections
  pickerSection: { marginBottom: 8 },
  pickerSectionLabel: { fontFamily: 'VastagoGrotesk', fontSize: 9, color: '#888', letterSpacing: 0.5, textTransform: 'uppercase' },
  pickerBlockTop: { marginTop: 4, marginBottom: 2 },
  pickerBlockDivider: {
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: '#ddd',
    marginTop: 12,
    paddingTop: 12,
  },
  pickerBlockTitle: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  radiusSliderHint: { fontFamily: 'VastagoGrotesk', fontSize: 8, color: '#aaa', marginTop: 2 },
  radiusSliderHeader: { flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', marginTop: 2 },
  radiusSliderValue: { fontFamily: 'VastagoGrotesk', fontSize: 12, color: '#333' },
  radiusSlider: { width: '100%', minWidth: 200, height: 36, marginVertical: 2 },
  swatchRow: { flexDirection: 'row', gap: 5, paddingVertical: 4 },
  swatch: {
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  swatchClear: { backgroundColor: '#f0f0f0' },
  swatchClearText: { fontSize: 10, color: '#999' },
  fontSizeRow: { flexDirection: 'row', gap: 4, marginTop: 4 },
  fontSizeBtn: {
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 5,
    borderWidth: 1,
    borderColor: '#ccc',
    backgroundColor: '#f5f5f5',
    alignItems: 'center',
  },
  fontSizeBtnText: { fontFamily: 'VastagoGrotesk', fontSize: 11, color: '#333' },
  textAlignRow: { flexDirection: 'row', gap: 4, marginTop: 4 },
  fontFamilyRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 4, marginTop: 4 },
  fontFamilyBtn: {
    borderWidth: 1,
    borderColor: '#dcdcdc',
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 6,
    backgroundColor: '#f5f5f5',
    maxWidth: 92,
  },
  fontFamilyBtnText: { fontFamily: 'VastagoGrotesk', fontSize: 10, color: '#333' },
  textAlignBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    borderWidth: 1,
    borderColor: '#dcdcdc',
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 6,
  },
  textAlignBtnText: { fontFamily: 'VastagoGrotesk', fontSize: 10, color: '#333' },
  // Merge row
  widgetPickerMergeRow: {
    flexDirection: 'row',
    marginTop: 4,
    gap: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mergeDropdownWrap: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  mergeDropdownTriggerWrap: { position: 'relative' },
  mergeDropdownLabel: { fontFamily: 'VastagoGrotesk', fontSize: 12, color: '#333' },
  mergeDropdownTrigger: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: '#b0b0b0',
    borderRadius: 6,
    backgroundColor: '#fff',
    minWidth: 100,
  },
  mergeDropdownTriggerText: {},
  mergeDropdownList: {
    position: 'absolute',
    top: 38,
    left: 0,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#b0b0b0',
    borderRadius: 6,
    minWidth: 100,
    zIndex: 10,
    elevation: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
  },
  mergeDropdownItem: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 8, paddingHorizontal: 10 },
  widgetPickerMergeBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingVertical: 4, paddingHorizontal: 8 },
  widgetPickerMergeLabel: { fontFamily: 'VastagoGrotesk', fontSize: 11 },
});
